import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiMail, FiMessageCircle, FiCheckCircle, FiEyeOff, FiCreditCard, FiPrinter, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiChevronDown, FiDownload, FiRefreshCw, FiEdit2, FiTrash2 } from 'react-icons/fi';
import api, { SeriesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import CustomerDetail from './CustomerDetail';
import PaymentModal from '../components/PaymentModal';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const INDIAN_STATE_CODE_TO_NAME = {
  '01': 'Jammu & Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Daman and Diu, Dadra & Nagar Haveli',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh (before bifurcation)',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh (New)',
  '38': 'Ladakh'
};

const getStateDisplayName = (stateValue) => {
  const raw = String(stateValue || '').trim();
  if (!raw || raw === '-') return '';
  const normalizedCode = /^\d+$/.test(raw) ? raw.padStart(2, '0') : raw;
  return INDIAN_STATE_CODE_TO_NAME[normalizedCode] || raw;
};

const getCurrentFiscalYearRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { start, end };
};

const isInvoicePaid = (row) => {
  const st = String(row?.status || '').toUpperCase();
  const paySt = String(row?.paymentStatus || '').toUpperCase();
  return st === 'PAID' || paySt === 'PAID';
};

const hasInvoicePayment = (row) => {
  const st = String(row?.status || '').toUpperCase();
  const paySt = String(row?.paymentStatus || '').toUpperCase();
  const paidAmt = Number(row?.amountPaid || 0);
  return st === 'PAID' || paySt === 'PAID' || paySt === 'PARTIALLY_PAID' || paidAmt > 0;
};

const isInvoiceEditable = (row) => !hasInvoicePayment(row) && String(row?.status || '').toUpperCase() !== 'CANCELLED';

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: colors.stone800,
    borderRadius: 12,
    padding: 14,
    border: `1px solid ${colors.stone700}`,
  },
  statTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone300,
    marginBottom: 10,
  },
  segBar: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.stone700,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
    marginTop: 10,
  },
  legendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 11,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  legendItemActive: {
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.1)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px 7px 32px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  select: {
    padding: '7px 26px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  input: {
    padding: '7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  ...tableStyles,
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent600} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  pageBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '10px 16px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  drawerHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone600,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
};

export default function Invoices() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    const toYMD = (iso) => new Date(iso).toISOString().slice(0,10);
    // Use URL params if available, otherwise use defaults
    const params = new URLSearchParams(window.location.search);
    const pFromDate = params.get('fromDate');
    const pToDate = params.get('toDate');
    const pStatus = params.get('status');
    const pCreatedFrom = params.get('createdFrom');
    return {
      status: pStatus || '',
      customerId: '',
      createdFrom: pCreatedFrom || '',
      fromDate: pFromDate || toYMD(start.toISOString()),
      toDate: pToDate || toYMD(end.toISOString()),
      number: ''
    };
  });
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); // keys: email:<id>, pdf:<id>, wa:<id>, regen-pdf:<id>
  const [toast, setToast] = useState('');
  const [waModal, setWaModal] = useState({ open: false, id: null, phone: '', message: '' });
  const [payModal, setPayModal] = useState({ open: false, id: null });
  const [payInfo, setPayInfo] = useState({ open: false, inv: null });
  const [seriesState, setSeriesState] = useState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: '', fySep: '/' });
  const [formatModal, setFormatModal] = useState(false);
  const [formatOptions, setFormatOptions] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [formatActivatingId, setFormatActivatingId] = useState(null);
  const [stats, setStats] = useState({ totalCount: 0, paidCount: 0, totalAmount: 0, paidAmount: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0, cessAmount: 0 });
  const [newInvModal, setNewInvModal] = useState(false);
  const [billPreview, setBillPreview] = useState({ open: false, invoiceId: null, invoiceNumber: '', snapshot: null });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', kind: 'Invoice', loading: false, invoiceId: null });
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, id: null, customer: null });
  const [exportModal, setExportModal] = useState(false);
  const [exportState, setExportState] = useState({
    running: false,
    stage: '',
    processed: 0,
    total: 0,
    error: ''
  });
  const [exportOptions, setExportOptions] = useState({
    scope: 'page', // page | all
    format: 'csv_summary', // csv_summary | csv_detailed | xlsx_detailed | json
    includeCustomerContact: true,
    includePayment: true,
    includeTaxBreakdown: false,
    includeNotes: false,
    includeItems: false,
    includeDiscountBreakdown: false
  });
  const exportCancelRef = useRef(false);
  const [showStats, setShowStats] = useState(() => {
    try {
      const raw = window.localStorage.getItem('invoices.showStats');
      return raw == null ? true : JSON.parse(raw);
    } catch (_) { return true; }
  });
  const [showQuickSearch, setShowQuickSearch] = useState(() => {
    try {
      const raw = window.localStorage.getItem('invoices.showQuickSearch');
      return raw == null ? true : JSON.parse(raw);
    } catch (_) { return true; }
  });
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pFromDate = params.get('fromDate');
    const pToDate = params.get('toDate');
    if (pFromDate && pToDate) {
      return { startDate: pFromDate, endDate: pToDate, label: 'Custom' };
    }
    const { start, end } = getCurrentFiscalYearRange();
    return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Current FY' };
  });



  const params = useMemo(() => {
    return { page, pageSize, ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) };
  }, [page, pageSize, filters]);

  const safeYMD = (val) => {
    if (!val) return '';
    try {
      // Supports YYYY-MM-DD or ISO
      const d = new Date(val);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      const s = String(val);
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      return s;
    } catch { return String(val); }
  };

  const csvEscape = (value) => {
    if (value == null) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const toCsv = ({ columns, rows }) => {
    const header = columns.map((c) => csvEscape(c.label)).join(',');
    const body = rows.map((r) => columns.map((c) => csvEscape(c.get(r))).join(',')).join('\n');
    // BOM for Excel UTF-8
    return '\uFEFF' + header + '\n' + body + '\n';
  };

  const downloadTextFile = ({ filename, content, mimeType }) => {
    try {
      const blob = new Blob([content], { type: mimeType || 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error(e);
      setToast('Failed to download file');
    }
  };

  const downloadBlobFile = ({ filename, blob }) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error(e);
      setToast('Failed to download file');
    }
  };

  const buildExportFilename = (ext) => {
    const parts = ['invoices'];
    const from = filters.fromDate ? `from-${filters.fromDate}` : null;
    const to = filters.toDate ? `to-${filters.toDate}` : null;
    if (from) parts.push(from);
    if (to) parts.push(to);
    if (filters.status) parts.push(`status-${String(filters.status).toLowerCase()}`);
    if (filters.createdFrom) parts.push(`source-${String(filters.createdFrom).toLowerCase()}`);
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    parts.push(ts);
    return parts.filter(Boolean).join('__') + '.' + ext;
  };

  const sanitizeInvoiceForExport = (inv) => {
    if (!inv || typeof inv !== 'object') return inv;
    const { customerId, ...rest } = inv;
    const cs = rest.customerSnapshot && typeof rest.customerSnapshot === 'object'
      ? (() => {
          const { id, customerId: csCustomerId, ...csRest } = rest.customerSnapshot;
          return csRest;
        })()
      : rest.customerSnapshot;
    return { ...rest, customerSnapshot: cs };
  };

  const sanitizedFiltersForExport = () => {
    const { customerId, ...rest } = (filters || {});
    return rest;
  };

  const normalizeInvoiceLines = (detail) => {
    const linesRaw = Array.isArray(detail?.items)
      ? detail.items
      : (Array.isArray(detail?.lineItems) ? detail.lineItems : (Array.isArray(detail?.lines) ? detail.lines : []));
    return (linesRaw || []).map((l) => {
      const qty = Number(l?.quantity ?? l?.qty ?? 0) || 0;
      const unitPrice = Number(l?.price ?? l?.rate ?? l?.unitPrice ?? 0) || 0;
      const gross = qty * unitPrice;
      const discAmt = Math.max(0, Number(l?.discountAmount ?? 0) || 0);
      const discPct = Math.max(0, Math.min(100, Number(l?.discountPercent ?? 0) || 0));
      const discFromPct = (discPct / 100) * gross;
      const netComputed = Math.max(0, gross - discAmt - discFromPct);
      const net = Number(l?.amount ?? l?.total ?? l?.lineTotal ?? l?.netAmount ?? NaN);
      return {
        name: l?.name || l?.itemSnapshot?.name || l?.itemName || l?.description || 'Item',
        sku: l?.sku || l?.itemSnapshot?.sku || '',
        hsn: l?.hsn || l?.hsnCode || l?.itemSnapshot?.hsn || '',
        qty,
        unitPrice,
        gross: Number.isFinite(net) ? Math.max(0, net) : +netComputed.toFixed(2),
        discountAmount: discAmt,
        discountPercent: discPct,
        taxRate: Number(l?.taxRate ?? l?.gstRate ?? l?.gstPercent ?? l?.taxPercent ?? NaN),
        raw: l
      };
    });
  };

  const computeDiscountBreakdown = (detail) => {
    try {
      const lines = normalizeInvoiceLines(detail);
      const grossTotal = lines.reduce((acc, l) => acc + (Number(l.qty || 0) * Number(l.unitPrice || 0)), 0);
      const lineDiscountTotal = lines.reduce((acc, l) => {
        const gross = Number(l.qty || 0) * Number(l.unitPrice || 0);
        const discAmt = Math.max(0, Number(l.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        return acc + Math.min(gross, discAmt + discFromPct);
      }, 0);
      const netBeforeDocDiscount = lines.reduce((acc, l) => {
        const gross = Number(l.qty || 0) * Number(l.unitPrice || 0);
        const discAmt = Math.max(0, Number(l.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        const lineDisc = Math.min(gross, discAmt + discFromPct);
        return acc + Math.max(0, gross - lineDisc);
      }, 0);
      const docDiscAmt = Math.max(0, Number(detail?.discountAmount || 0));
      const docDiscPct = Math.max(0, Math.min(100, Number(detail?.discountPercent || 0)));
      const docDiscountAmount = docDiscPct > 0 ? +((netBeforeDocDiscount * docDiscPct) / 100).toFixed(2) : +docDiscAmt.toFixed(2);
      const totalDiscount = +(Number(lineDiscountTotal || 0) + Number(docDiscountAmount || 0)).toFixed(2);
      return {
        grossTotal: +grossTotal.toFixed(2),
        lineDiscountTotal: +lineDiscountTotal.toFixed(2),
        netBeforeDocDiscount: +netBeforeDocDiscount.toFixed(2),
        docDiscountPercent: +docDiscPct.toFixed(2),
        docDiscountAmount: +docDiscountAmount.toFixed(2),
        totalDiscount
      };
    } catch (_) {
      return {
        grossTotal: '',
        lineDiscountTotal: '',
        netBeforeDocDiscount: '',
        docDiscountPercent: '',
        docDiscountAmount: '',
        totalDiscount: ''
      };
    }
  };

  const formatItemsInline = (detail) => {
    const lines = normalizeInvoiceLines(detail);
    if (!lines.length) return '';
    // Keep it compact for CSV cell
    return lines
      .map((l) => {
        const qty = Number(l.qty || 0);
        const name = String(l.name || '').trim();
        return qty ? `${name} x${qty}` : name;
      })
      .filter(Boolean)
      .join(' | ');
  };

  const runExport = async () => {
    if (exportState.running) return;
    exportCancelRef.current = false;
    setExportState({ running: true, stage: 'Preparing…', processed: 0, total: 0, error: '' });

    const isCancelled = () => exportCancelRef.current;

    const baseFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const listPageSize = exportOptions.scope === 'all' ? 200 : pageSize;

    const fetchAllInvoicesMatchingFilters = async () => {
      if (exportOptions.scope === 'page') {
        return { list: invoices || [], total: (invoices || []).length };
      }

      let p = 1;
      let acc = [];
      let grandTotal = 0;

      while (true) {
        if (isCancelled()) break;
        setExportState((s) => ({ ...s, stage: `Fetching invoices (page ${p})…`, processed: acc.length, total: grandTotal || 0 }));
        const { data } = await api.get('/api/invoices', { params: { ...baseFilters, page: p, pageSize: listPageSize } });
        const pageData = Array.isArray(data?.data) ? data.data : [];
        grandTotal = Number(data?.total || grandTotal || 0);
        acc = acc.concat(pageData);
        if (!pageData.length) break;
        if (acc.length >= grandTotal) break;
        p += 1;
        // Safety cap to avoid infinite loops
        if (p > 500) break;
      }

      return { list: acc, total: grandTotal || acc.length };
    };

    const fetchInvoiceDetailsPool = async (ids, concurrency = 4) => {
      const results = [];
      let idx = 0;

      const worker = async () => {
        while (idx < ids.length) {
          if (isCancelled()) return;
          const my = idx++;
          const id = ids[my];
          try {
            setExportState((s) => ({ ...s, stage: 'Fetching invoice details…', processed: my, total: ids.length }));
            const { data } = await api.get(`/api/invoices/${id}`);
            const detail = data?.invoice || data || {};
            results[my] = detail;
          } catch (e) {
            console.error('Export: failed invoice detail', id, e);
            results[my] = null;
          }
        }
      };

      const workers = Array.from({ length: Math.min(concurrency, ids.length) }, () => worker());
      await Promise.all(workers);
      return results.filter(Boolean);
    };

    try {
      const { list: listInvoices } = await fetchAllInvoicesMatchingFilters();
      if (isCancelled()) {
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('Export cancelled');
        return;
      }

      if (!listInvoices.length) {
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('No invoices to export for these filters');
        return;
      }

      if (exportOptions.format === 'json') {
        let invoicesPayload = listInvoices;
        if (exportOptions.includeItems || exportOptions.includeTaxBreakdown || exportOptions.includeDiscountBreakdown) {
          const ids = listInvoices.map((i) => i?.id).filter(Boolean);
          setExportState((s) => ({ ...s, stage: 'Fetching invoice details…', processed: 0, total: ids.length }));
          const details = await fetchInvoiceDetailsPool(ids, 4);
          // Preserve base list fields but include items (line items) from detail.
          const detailMap = new Map(details.map((d) => [d.id, d]));
          invoicesPayload = listInvoices.map((inv) => {
            const d = detailMap.get(inv.id);
            if (!d) return sanitizeInvoiceForExport(inv);
            const items = Array.isArray(d.items) ? d.items : (Array.isArray(d.lineItems) ? d.lineItems : []);
            const discountBreakdown = exportOptions.includeDiscountBreakdown ? computeDiscountBreakdown(d) : undefined;
            const taxBreakdown = exportOptions.includeTaxBreakdown ? {
              taxableValue: d?.taxableValue ?? '',
              cgstAmount: d?.cgstAmount ?? '',
              sgstAmount: d?.sgstAmount ?? '',
              igstAmount: d?.igstAmount ?? '',
              cessAmount: d?.cessAmount ?? ''
            } : undefined;
            return sanitizeInvoiceForExport({
              ...inv,
              ...(exportOptions.includeItems ? { items } : {}),
              ...(exportOptions.includeDiscountBreakdown ? { discountBreakdown } : {}),
              ...(exportOptions.includeTaxBreakdown ? { taxBreakdown } : {})
            });
          });
        } else {
          invoicesPayload = listInvoices.map(sanitizeInvoiceForExport);
        }

        setExportState((s) => ({ ...s, stage: 'Building JSON…', processed: 0, total: invoicesPayload.length }));
        const payload = {
          exportedAt: new Date().toISOString(),
          companyId: company?.id || null,
          scope: exportOptions.scope,
          filters: sanitizedFiltersForExport(),
          count: invoicesPayload.length,
          invoices: invoicesPayload
        };
        downloadTextFile({
          filename: buildExportFilename('json'),
          content: JSON.stringify(payload, null, 2),
          mimeType: 'application/json;charset=utf-8'
        });
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('Exported JSON');
        setExportModal(false);
        return;
      }

      if (exportOptions.format === 'csv_summary') {
        let itemsInlineById = new Map();
        let itemCountById = new Map();
        let taxById = new Map();
        let discById = new Map();
        let detailTotalById = new Map();
        if (exportOptions.includeItems || exportOptions.includeTaxBreakdown || exportOptions.includeDiscountBreakdown) {
          const ids = listInvoices.map((i) => i?.id).filter(Boolean);
          setExportState((s) => ({ ...s, stage: 'Fetching invoice details…', processed: 0, total: ids.length }));
          const details = await fetchInvoiceDetailsPool(ids, 4);
          for (const d of details) {
            try {
              detailTotalById.set(d.id, d?.total ?? '');
              if (exportOptions.includeItems) {
                itemsInlineById.set(d.id, formatItemsInline(d));
                itemCountById.set(d.id, normalizeInvoiceLines(d).length);
              }
              if (exportOptions.includeTaxBreakdown) {
                taxById.set(d.id, {
                  taxableValue: d?.taxableValue ?? '',
                  cgstAmount: d?.cgstAmount ?? '',
                  sgstAmount: d?.sgstAmount ?? '',
                  igstAmount: d?.igstAmount ?? '',
                  cessAmount: d?.cessAmount ?? ''
                });
              }
              if (exportOptions.includeDiscountBreakdown) {
                discById.set(d.id, computeDiscountBreakdown(d));
              }
            } catch (_) {}
          }
        }

        setExportState((s) => ({ ...s, stage: 'Building CSV…', processed: 0, total: listInvoices.length }));
        const columns = [
          { key: 'number', label: 'Invoice Number', get: (r) => r?.number || '' },
          { key: 'date', label: 'Invoice Date', get: (r) => safeYMD(r?.date) },
          { key: 'dueDate', label: 'Due Date', get: (r) => safeYMD(r?.dueDate) },
          { key: 'status', label: 'Status', get: (r) => r?.status || '' },
          { key: 'createdFrom', label: 'Created From', get: (r) => r?.createdFrom || '' },
          { key: 'customerName', label: 'Customer Name', get: (r) => r?.customerSnapshot?.name || '' },
          ...(exportOptions.includeItems ? [
            { key: 'itemCount', label: 'Item Count', get: (r) => itemCountById.get(r?.id) ?? '' },
            { key: 'items', label: 'Items', get: (r) => itemsInlineById.get(r?.id) ?? '' }
          ] : []),
          ...(exportOptions.includeCustomerContact ? [
            { key: 'customerPhone', label: 'Customer Phone', get: (r) => r?.customerSnapshot?.phone || r?.customerSnapshot?.mobile || r?.customerSnapshot?.whatsapp || '' },
            { key: 'customerEmail', label: 'Customer Email', get: (r) => r?.customerSnapshot?.email || '' }
          ] : []),
          ...(exportOptions.includeDiscountBreakdown ? [
            { key: 'grossTotal', label: 'Gross Total (Before Discounts)', get: (r) => discById.get(r?.id)?.grossTotal ?? '' },
            { key: 'lineDiscountTotal', label: 'Line Discount Total', get: (r) => discById.get(r?.id)?.lineDiscountTotal ?? '' },
            { key: 'netBeforeDocDiscount', label: 'Net Before Doc Discount', get: (r) => discById.get(r?.id)?.netBeforeDocDiscount ?? '' },
            { key: 'docDiscountPercent', label: 'Doc Discount %', get: (r) => discById.get(r?.id)?.docDiscountPercent ?? '' },
            { key: 'docDiscountAmount', label: 'Doc Discount Amount', get: (r) => discById.get(r?.id)?.docDiscountAmount ?? '' },
            { key: 'totalDiscount', label: 'Total Discount', get: (r) => discById.get(r?.id)?.totalDiscount ?? '' }
          ] : []),
          ...(exportOptions.includeTaxBreakdown ? [
            { key: 'taxableValue', label: 'Taxable Value', get: (r) => taxById.get(r?.id)?.taxableValue ?? '' },
            { key: 'cgstAmount', label: 'CGST Amount', get: (r) => taxById.get(r?.id)?.cgstAmount ?? '' },
            { key: 'sgstAmount', label: 'SGST Amount', get: (r) => taxById.get(r?.id)?.sgstAmount ?? '' },
            { key: 'igstAmount', label: 'IGST Amount', get: (r) => taxById.get(r?.id)?.igstAmount ?? '' },
            { key: 'cessAmount', label: 'Cess Amount', get: (r) => taxById.get(r?.id)?.cessAmount ?? '' }
          ] : []),
          { key: 'total', label: 'Invoice Total', get: (r) => detailTotalById.get(r?.id) ?? (r?.total ?? '') },
          ...(exportOptions.includePayment ? [
            { key: 'amountPaid', label: 'Amount Paid', get: (r) => r?.amountPaid ?? (r?.status === 'PAID' ? r?.total : '') },
            { key: 'paymentMethod', label: 'Payment Method', get: (r) => r?.paymentMethod || '' },
            { key: 'paymentReference', label: 'Payment Reference', get: (r) => r?.paymentReference || '' },
            { key: 'paymentDate', label: 'Payment Date', get: (r) => safeYMD(r?.paymentDate) }
          ] : []),
          ...(exportOptions.includeNotes ? [
            { key: 'notes', label: 'Notes', get: (r) => r?.notes || '' }
          ] : [])
        ];

        const csv = toCsv({ columns, rows: listInvoices });
        downloadTextFile({
          filename: buildExportFilename('csv'),
          content: csv,
          mimeType: 'text/csv;charset=utf-8'
        });
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('Exported CSV');
        setExportModal(false);
        return;
      }

      // Detailed (line-items) export: CSV or XLSX
      const isXlsxDetailed = exportOptions.format === 'xlsx_detailed';
      setExportState((s) => ({ ...s, stage: `Preparing detailed ${isXlsxDetailed ? 'XLSX' : 'CSV'} export…`, processed: 0, total: listInvoices.length }));
      const ids = listInvoices.map((i) => i?.id).filter(Boolean);
      const details = await fetchInvoiceDetailsPool(ids, 4);
      if (isCancelled()) {
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('Export cancelled');
        return;
      }

      setExportState((s) => ({ ...s, stage: `Building detailed ${isXlsxDetailed ? 'XLSX' : 'CSV'}…`, processed: 0, total: details.length }));
      const rows = [];
      for (let i = 0; i < details.length; i++) {
        if (isCancelled()) break;
        setExportState((s) => ({ ...s, processed: i + 1, total: details.length }));
        const d = details[i] || {};
        const inv = listInvoices.find((x) => x?.id === d?.id) || listInvoices[i] || {};
        const lines = normalizeInvoiceLines(d);
        if (!lines.length) {
          rows.push({
            invoice: inv,
            detail: d,
            line: null,
            isFirstForInvoice: true
          });
        } else {
          for (let li = 0; li < lines.length; li++) {
            const line = lines[li];
            rows.push({ invoice: inv, detail: d, line, isFirstForInvoice: li === 0 });
          }
        }
      }

      // For duplicate invoice rows (line items):
      // - Keep Invoice Number + Customer Name visible on every line.
      // - Suppress other invoice-level fields on duplicates by showing '-'.
      const dashOnDuplicate = (r, val) => (r?.isFirstForInvoice ? val : '-');
      const columns = [
        // Put key fields first so Excel users can freeze them.
        { key: 'invoiceNumber', label: 'Invoice Number', get: (r) => r?.invoice?.number || r?.detail?.number || '' },
        { key: 'customerName', label: 'Customer Name', get: (r) => r?.detail?.customerSnapshot?.name || r?.invoice?.customerSnapshot?.name || '' },
        { key: 'invoiceDate', label: 'Invoice Date', get: (r) => dashOnDuplicate(r, safeYMD(r?.detail?.date || r?.invoice?.date)) },
        { key: 'dueDate', label: 'Due Date', get: (r) => dashOnDuplicate(r, safeYMD(r?.detail?.dueDate || r?.invoice?.dueDate)) },
        { key: 'status', label: 'Status', get: (r) => dashOnDuplicate(r, r?.detail?.status || r?.invoice?.status || '') },
        { key: 'createdFrom', label: 'Created From', get: (r) => dashOnDuplicate(r, r?.detail?.createdFrom || r?.invoice?.createdFrom || '') },
        ...(exportOptions.includeCustomerContact ? [
          { key: 'customerPhone', label: 'Customer Phone', get: (r) => dashOnDuplicate(r, r?.detail?.customerSnapshot?.phone || r?.detail?.customerSnapshot?.mobile || r?.detail?.customerSnapshot?.whatsapp || r?.invoice?.customerSnapshot?.phone || '') },
          { key: 'customerEmail', label: 'Customer Email', get: (r) => dashOnDuplicate(r, r?.detail?.customerSnapshot?.email || r?.invoice?.customerSnapshot?.email || '') }
        ] : []),
        ...(exportOptions.includeDiscountBreakdown ? [
          { key: 'grossTotal', label: 'Gross Total (Before Discounts)', get: (r) => {
            const d = computeDiscountBreakdown(r?.detail);
            return dashOnDuplicate(r, d?.grossTotal ?? '');
          }},
          { key: 'lineDiscountTotal', label: 'Line Discount Total', get: (r) => {
            const d = computeDiscountBreakdown(r?.detail);
            return dashOnDuplicate(r, d?.lineDiscountTotal ?? '');
          }},
          { key: 'docDiscountPercent', label: 'Doc Discount %', get: (r) => {
            const d = computeDiscountBreakdown(r?.detail);
            return dashOnDuplicate(r, d?.docDiscountPercent ?? '');
          }},
          { key: 'docDiscountAmount', label: 'Doc Discount Amount', get: (r) => {
            const d = computeDiscountBreakdown(r?.detail);
            return dashOnDuplicate(r, d?.docDiscountAmount ?? '');
          }},
          { key: 'totalDiscount', label: 'Total Discount', get: (r) => {
            const d = computeDiscountBreakdown(r?.detail);
            return dashOnDuplicate(r, d?.totalDiscount ?? '');
          }}
        ] : []),
        ...(exportOptions.includeTaxBreakdown ? [
          { key: 'taxableValue', label: 'Taxable Value', get: (r) => dashOnDuplicate(r, r?.detail?.taxableValue ?? '') },
          { key: 'cgstAmount', label: 'CGST Amount', get: (r) => dashOnDuplicate(r, r?.detail?.cgstAmount ?? '') },
          { key: 'sgstAmount', label: 'SGST Amount', get: (r) => dashOnDuplicate(r, r?.detail?.sgstAmount ?? '') },
          { key: 'igstAmount', label: 'IGST Amount', get: (r) => dashOnDuplicate(r, r?.detail?.igstAmount ?? '') },
          { key: 'cessAmount', label: 'Cess Amount', get: (r) => dashOnDuplicate(r, r?.detail?.cessAmount ?? '') }
        ] : []),
        { key: 'invoiceTotal', label: 'Invoice Total', get: (r) => dashOnDuplicate(r, r?.detail?.total ?? r?.invoice?.total ?? '') },
        ...(exportOptions.includePayment ? [
          { key: 'amountPaid', label: 'Amount Paid', get: (r) => dashOnDuplicate(r, r?.detail?.amountPaid ?? r?.invoice?.amountPaid ?? '') },
          { key: 'paymentMethod', label: 'Payment Method', get: (r) => dashOnDuplicate(r, r?.detail?.paymentMethod || r?.invoice?.paymentMethod || '') },
          { key: 'paymentReference', label: 'Payment Reference', get: (r) => dashOnDuplicate(r, r?.detail?.paymentReference || r?.invoice?.paymentReference || '') },
          { key: 'paymentDate', label: 'Payment Date', get: (r) => dashOnDuplicate(r, safeYMD(r?.detail?.paymentDate || r?.invoice?.paymentDate)) }
        ] : []),
        ...(exportOptions.includeNotes ? [
          { key: 'notes', label: 'Notes', get: (r) => dashOnDuplicate(r, r?.detail?.notes || r?.invoice?.notes || '') }
        ] : []),
        { key: 'lineName', label: 'Line Item Name', get: (r) => r?.line?.name || '' },
        { key: 'sku', label: 'SKU', get: (r) => r?.line?.sku || '' },
        { key: 'hsn', label: 'HSN', get: (r) => r?.line?.hsn || '' },
        { key: 'qty', label: 'Quantity', get: (r) => r?.line?.qty ?? '' },
        { key: 'unitPrice', label: 'Unit Price', get: (r) => r?.line?.unitPrice ?? '' },
        { key: 'lineGross', label: 'Line Gross', get: (r) => {
          const q = Number(r?.line?.qty ?? 0);
          const p = Number(r?.line?.unitPrice ?? 0);
          return (Number.isFinite(q) && Number.isFinite(p)) ? +(q * p).toFixed(2) : '';
        }},
        { key: 'discountAmount', label: 'Line Discount Amount', get: (r) => r?.line?.discountAmount ?? '' },
        { key: 'discountPercent', label: 'Line Discount Percent', get: (r) => r?.line?.discountPercent ?? '' },
        { key: 'taxRate', label: 'Tax Rate', get: (r) => Number.isFinite(r?.line?.taxRate) ? r.line.taxRate : '' },
        { key: 'lineNet', label: 'Line Net (After Discounts)', get: (r) => r?.line?.gross ?? '' }
      ];

      if (!isXlsxDetailed) {
        const csv = toCsv({ columns, rows });
        downloadTextFile({
          filename: buildExportFilename('csv'),
          content: csv,
          mimeType: 'text/csv;charset=utf-8'
        });
        setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
        setToast('Exported detailed CSV');
        setExportModal(false);
        return;
      }

      // XLSX detailed export with frozen panes (Invoice # + Customer Name + header row)
      setExportState((s) => ({ ...s, stage: 'Generating XLSX…' }));
      const mod = await import('exceljs');
      const ExcelJS = mod?.default || mod;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Invoices (Detailed)', {
        views: [{ state: 'frozen', xSplit: 2, ySplit: 1 }]
      });

      // Header
      ws.addRow(columns.map((c) => c.label));
      ws.getRow(1).font = { bold: true };

      // Rows
      const numericKeys = new Set([
        'taxableValue', 'cgstAmount', 'sgstAmount', 'igstAmount', 'cessAmount',
        'invoiceTotal', 'amountPaid',
        'qty', 'unitPrice', 'lineGross', 'discountAmount', 'discountPercent', 'taxRate', 'lineNet'
      ]);

      for (const r of rows) {
        const vals = columns.map((c) => {
          const v = c.get(r);
          if (v === '-' || v === '' || v == null) return v ?? '';
          if (numericKeys.has(c.key)) {
            const num = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(num) ? num : v;
          }
          return v;
        });
        ws.addRow(vals);
      }

      // Column widths (simple heuristic)
      ws.columns = columns.map((c, idx) => {
        const base = Math.max(10, Math.min(40, (c.label || '').length + 6));
        const extra = (idx < 6) ? 2 : 0;
        return { width: base + extra };
      });

      // Number formats for common money columns
      const moneyCols = ['taxableValue', 'cgstAmount', 'sgstAmount', 'igstAmount', 'cessAmount', 'invoiceTotal', 'amountPaid', 'unitPrice', 'lineGross', 'discountAmount', 'lineNet'];
      for (let ci = 0; ci < columns.length; ci++) {
        const key = columns[ci].key;
        if (!moneyCols.includes(key)) continue;
        ws.getColumn(ci + 1).numFmt = '0.00';
      }

      const buf = await wb.xlsx.writeBuffer();
      downloadBlobFile({
        filename: buildExportFilename('xlsx'),
        blob: new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      });
      setExportState({ running: false, stage: '', processed: 0, total: 0, error: '' });
      setToast('Exported detailed XLSX');
      setExportModal(false);
    } catch (e) {
      console.error('Export failed', e);
      setExportState({ running: false, stage: '', processed: 0, total: 0, error: 'Export failed' });
      setToast('Export failed');
    }
  };

  const cancelExport = () => {
    exportCancelRef.current = true;
    setExportState((s) => ({ ...s, stage: 'Cancelling…' }));
  };

  const localStatusCounts = useMemo(() => {
    const counts = { DRAFT: 0, APPROVED: 0, SENT: 0, PAID: 0, OVERDUE: 0, CANCELLED: 0, OTHER: 0 };
    for (const inv of (invoices || [])) {
      const s = String(inv?.status || '').toUpperCase();
      if (!s) { counts.OTHER += 1; continue; }
      if (counts[s] != null) counts[s] += 1;
      else counts.OTHER += 1;
    }
    return counts;
  }, [invoices]);

  const localCreatedFromCounts = useMemo(() => {
    const counts = { DIRECT: 0, POS: 0, CONVERTED_QUOTE: 0, CUSTOMER_ORDER: 0, QUOTE_ADVANCE: 0, RECURRING_PROFORMA: 0, OTHER: 0 };
    for (const inv of (invoices || [])) {
      const src = String(inv?.createdFrom || '').toUpperCase();
      if (!src) { counts.OTHER += 1; continue; }
      if (counts[src] != null) counts[src] += 1;
      else counts.OTHER += 1;
    }
    return counts;
  }, [invoices]);

  const localCreatedFromAmounts = useMemo(() => {
    const sums = { DIRECT: 0, POS: 0, CONVERTED_QUOTE: 0, CUSTOMER_ORDER: 0, QUOTE_ADVANCE: 0, RECURRING_PROFORMA: 0, OTHER: 0 };
    for (const inv of (invoices || [])) {
      const status = String(inv?.status || '').toUpperCase();
      if (status === 'CANCELLED') continue;
      const src = String(inv?.createdFrom || '').toUpperCase();
      const amt = Number(inv?.total || 0) || 0;
      if (!src) { sums.OTHER += amt; continue; }
      if (sums[src] != null) sums[src] += amt;
      else sums.OTHER += amt;
    }
    return sums;
  }, [invoices]);

  const localCreatedFromPaidAmounts = useMemo(() => {
    const sums = { DIRECT: 0, POS: 0, CONVERTED_QUOTE: 0, CUSTOMER_ORDER: 0, QUOTE_ADVANCE: 0, RECURRING_PROFORMA: 0, OTHER: 0 };
    for (const inv of (invoices || [])) {
      const status = String(inv?.status || '').toUpperCase();
      if (status !== 'PAID') continue;
      const src = String(inv?.createdFrom || '').toUpperCase();
      const amt = Number(inv?.total || 0) || 0;
      if (!src) { sums.OTHER += amt; continue; }
      if (sums[src] != null) sums[src] += amt;
      else sums.OTHER += amt;
    }
    return sums;
  }, [invoices]);

  const fetchCustomers = async () => {
    if (!company?.id) return;
    setCustomersLoading(true);
    try {
      const { data } = await api.get(`/api/companies/${company.id}/customers`);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.customers) ? data.customers : (Array.isArray(data?.data) ? data.data : []));
      setCustomers((list || []).map(c => ({ id: c.id, name: c.name, phone: c.phone || c.mobile || c.whatsapp || '' })));
    } catch (e) {
      console.error(e);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/invoices', { params });
      setInvoices(data.data || []);
      setTotal(data.total || 0);
      if (data.stats) setStats(data.stats);
    } catch (e) {
      console.error(e);
      setToast('Failed to load invoices');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchInvoices(); }, [page, pageSize, filters]);

  useEffect(() => {
    if (!company?.id) return;
    fetchCustomers();
    (async () => {
      setSeriesState(s => ({ ...s, loading: true, error: '' }));
      try {
        const cur = await SeriesAPI.current(company.id, 'INVOICE');
        const fyFromBounds = (() => {
          try {
            if (cur.fyStart && cur.fyEnd) {
              const y1 = String(new Date(cur.fyStart).getUTCFullYear()).slice(-2);
              const y2 = String(new Date(cur.fyEnd).getUTCFullYear()).slice(-2);
              return `${y1}-${y2}`;
            }
            return '';
          } catch { return ''; }
        })();
        const fullPrefix = cur.prefix || '';
        const fyIdx = fyFromBounds ? fullPrefix.indexOf(fyFromBounds) : -1;
        let prefixPre = fullPrefix;
        let fySep = '/';
        if (fyIdx >= 0) {
          prefixPre = fullPrefix.slice(0, fyIdx);
          const charBefore = fullPrefix[fyIdx - 1];
          const charAfter = fullPrefix[fyIdx + fyFromBounds.length];
          if (charBefore && charBefore === charAfter && '/-_ .'.includes(charBefore)) {
            fySep = charBefore;
            if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1);
          } else if (charAfter && '/-_ .'.includes(charAfter)) {
            fySep = charAfter;
          } else if (charBefore && '/-_ .'.includes(charBefore)) {
            fySep = charBefore;
            if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1);
          }
        }
        setSeriesState({
          loading: false,
          saving: false,
          id: cur.id,
          prefix: fullPrefix,
          prefixPre,
          suffix: cur.suffix || '',
          currentSeq: cur.currentSeq || 0,
          error: '',
          fy: fyFromBounds,
          fySep
        });
      } catch (err) {
        const computeFY = (d) => {
          try {
            const dt = new Date(d || new Date());
            const yr = dt.getUTCFullYear();
            const mon = dt.getUTCMonth() + 1;
            const start = mon >= 4 ? yr : yr - 1;
            const end = mon >= 4 ? yr + 1 : yr;
            return `${String(start).slice(-2)}-${String(end).slice(-2)}`;
          } catch { return ''; }
        };
        setSeriesState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: computeFY(), fySep: '/' });
      }
    })();
  }, [company?.id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    try { window.localStorage.setItem('invoices.showStats', JSON.stringify(!!showStats)); } catch (_) {}
  }, [showStats]);
  useEffect(() => {
    try { window.localStorage.setItem('invoices.showQuickSearch', JSON.stringify(!!showQuickSearch)); } catch (_) {}
  }, [showQuickSearch]);

  const formatINR = (val) => {
    try {
      const num = Number(val || 0);
      return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num);
    } catch { return val; }
  };

  const handleDateChange = (val) => {
    if (val === 'all') {
      setDateRange({ startDate: null, endDate: null });
      setFilters((f) => ({ ...f, fromDate: '', toDate: '' }));
      setPage(1);
      return;
    }
    const toYMD = (iso) => new Date(iso).toISOString().slice(0,10);
    setDateRange(val);
    setFilters((f) => ({ ...f, fromDate: toYMD(val.startDate), toDate: toYMD(val.endDate) }));
    setPage(1);
  };

  const saveSeriesFormat = async () => {
    if (!company?.id) return;
    setSeriesState(s => ({ ...s, saving: true, error: '' }));
    try {
      const composedPrefix = `${seriesState.prefixPre || ''}${seriesState.fySep || '/'}${seriesState.fy || ''}${seriesState.fySep || '/'}`;
      const targetDate = seriesState.fy ? `20${seriesState.fy.slice(0,2)}-04-01` : new Date().toISOString().slice(0,10);
      await SeriesAPI.setFormat({ companyId: company.id, documentType: 'INVOICE', prefix: composedPrefix, suffix: seriesState.suffix || '', date: targetDate });
      const [cur, list] = await Promise.all([
        SeriesAPI.current(company.id, 'INVOICE'),
        SeriesAPI.list({ companyId: company.id, documentType: 'INVOICE' })
      ]);
      setFormatOptions(Array.isArray(list) ? list : []);
      setSeriesState(s => ({ ...s, saving: false, id: cur.id, prefix: cur.prefix || composedPrefix, currentSeq: cur.currentSeq || s.currentSeq }));
      setToast('Invoice number format set');
      setFormatModal(false);
      setSelectedFormatId(null);
    } catch (e) {
      console.error(e);
      setSeriesState(s => ({ ...s, saving: false, error: 'Failed to save format' }));
    }
  };
  // Keep example in sync with latest sequence for the MODIFIED format using loaded options (no extra API, no debounce)
  useEffect(() => {
    if (!company?.id || !formatModal) return;
    try {
      const composedPrefix = `${seriesState.prefixPre || ''}${seriesState.fySep || '/'}${seriesState.fy || ''}${seriesState.fySep || '/'}`;
      const fy = seriesState.fy;
      const fyStart = fy ? `20${fy.slice(0,2)}-04-01` : null;
      const fyEnd = fy ? `20${fy.slice(3,5)}-03-31` : null;
      let match = null;
      if (Array.isArray(formatOptions) && formatOptions.length) {
        match = formatOptions.find(r =>
          r.documentType === 'INVOICE' &&
          r.prefix === composedPrefix &&
          (r.suffix || '') === (seriesState.suffix || '') &&
          (!!fyStart ? String(r.fyStart || '').startsWith(fyStart) : true) &&
          (!!fyEnd ? String(r.fyEnd || '').startsWith(fyEnd) : true)
        );
      }
      setSeriesState(s => ({ ...s, currentSeq: Number((match && match.currentSeq) ? match.currentSeq : 0) }));
    } catch (_) { /* ignore */ }
  }, [company?.id, formatModal, seriesState.prefixPre, seriesState.suffix, seriesState.fySep, seriesState.fy, formatOptions]);

  // Deselect "Selected" tag if user edits away from the selected row
  useEffect(() => {
    if (!selectedFormatId) return;
    const sel = (formatOptions || []).find(r => r.id === selectedFormatId);
    if (!sel) { setSelectedFormatId(null); return; }
    const composed = `${seriesState.prefixPre || ''}${seriesState.fySep || '/'}${seriesState.fy || ''}${seriesState.fySep || '/'}`;
    if (sel.prefix !== composed || (sel.suffix || '') !== (seriesState.suffix || '')) {
      setSelectedFormatId(null);
    }
  }, [seriesState.prefixPre, seriesState.fySep, seriesState.fy, seriesState.suffix, selectedFormatId, formatOptions]);

  // Load existing formats for selection
  useEffect(() => {
    if (!company?.id || !formatModal) return;
    (async () => {
      try {
        const list = await SeriesAPI.list({ companyId: company.id, documentType: 'INVOICE' });
        setFormatOptions(Array.isArray(list) ? list : []);
      } catch (_) { setFormatOptions([]); }
    })();
  }, [company?.id, formatModal]);

  const applyExistingFormat = async (row) => {
    try {
      setFormatActivatingId(row.id || null);
      const rowFy = (() => {
        if (row.fyStart && row.fyEnd) {
          const y1 = String(new Date(row.fyStart).getUTCFullYear()).slice(-2);
          const y2 = String(new Date(row.fyEnd).getUTCFullYear()).slice(-2);
          return `${y1}-${y2}`;
        }
        return '';
      })();
      const curFy = seriesState.fy || rowFy;
      const fullPrefix = row.prefix || '';
      const idx = rowFy ? fullPrefix.indexOf(rowFy) : -1;
      let prefixPre = fullPrefix;
      let fySep = seriesState.fySep || '/';
      if (idx >= 0) {
        prefixPre = fullPrefix.slice(0, idx);
        const charBefore = fullPrefix[idx - 1];
        const charAfter = fullPrefix[idx + rowFy.length];
        if (charBefore && charBefore === charAfter && '/-_ .'.includes(charBefore)) {
          fySep = charBefore;
          if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1);
        } else if (charAfter && '/-_ .'.includes(charAfter)) {
          fySep = charAfter;
        } else if (charBefore && '/-_ .'.includes(charBefore)) {
          fySep = charBefore;
          if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1);
        }
      }
      // Immediately activate this format server-side
      try {
        const targetDate = row.fyStart ? String(row.fyStart).slice(0,10) : (curFy ? `20${curFy.slice(0,2)}-04-01` : new Date().toISOString().slice(0,10));
        await SeriesAPI.setFormat({ companyId: company.id, documentType: 'INVOICE', prefix: row.prefix, suffix: row.suffix || '', date: targetDate });
      } catch (_) { /* ignore */ }
      // Refresh list and current
      try {
        const [cur, list] = await Promise.all([
          SeriesAPI.current(company.id, 'INVOICE'),
          SeriesAPI.list({ companyId: company.id, documentType: 'INVOICE' })
        ]);
        setFormatOptions(Array.isArray(list) ? list : []);
        setSeriesState(s => ({ ...s, id: cur.id, currentSeq: cur.currentSeq || s.currentSeq }));
      } catch (_) { /* ignore */ }
      setSeriesState(s => ({
        ...s,
        prefixPre,
        suffix: row.suffix || '',
        fy: curFy,
        fySep,
        currentSeq: Number(row.currentSeq || s.currentSeq || 0)
      }));
      setSelectedFormatId(row.id || null);
    } catch (_) { /* ignore */ }
    finally {
      setFormatActivatingId(null);
    }
  };

  const handleDownloadPdf = async (id) => {
    const key = `pdf:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.get(`/api/invoices/${id}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      console.error('Failed to open PDF', e);
      setToast('Failed to open PDF');
    } finally { setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const handleRegeneratePdf = async (inv) => {
    const key = `regen-pdf:${inv.id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post(`/api/invoices/${inv.id}/regenerate-pdf`);
      const url = data?.url;
      if (data?.renumbered && data?.number) {
        setToast(`Invoice renumbered to ${data.number} and PDF regenerated`);
        fetchInvoices();
      } else {
        setToast('Invoice PDF regenerated');
      }
      if (pdfPreview.open && pdfPreview.kind === 'Invoice' && pdfPreview.invoiceId === inv.id) {
        setPdfPreview((p) => ({ ...p, url: url || p.url, loading: false }));
      }
    } catch (e) {
      console.error('Failed to regenerate PDF', e);
      // Number may have been fixed even if PDF/S3 failed
      if (e?.response?.data?.renumbered && e?.response?.data?.number) {
        setToast(`Invoice renumbered to ${e.response.data.number}; PDF failed: ${e.response.data.error || 'unknown error'}`);
        fetchInvoices();
      } else {
        setToast(e?.response?.data?.error || 'Failed to regenerate PDF');
      }
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };
  const handleSendEmail = async (id) => {
    const key = `email:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      await api.post(`/api/invoices/${id}/send`);
      setToast('Email sent');
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setToast('Failed to send email');
    } finally { setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openWhatsAppModal = async (inv) => {
    const phone = (inv.customerSnapshot?.phone || inv.customerSnapshot?.mobile || inv.customerSnapshot?.whatsapp || '').toString();
    let message = `Hello ${inv.customerSnapshot?.name || ''}, here is Invoice ${inv.number} dated ${inv.date} for ${formatINR(inv.total)}.`;
    try {
      const { data } = await api.get(`/api/invoices/${inv.id}/pdf-url`);
      const url = data?.url;
      if (url) {
        message += `\nPDF: ${url}`;
      }
    } catch (e) {
      // ignore URL fetch errors; keep base message
    }
    message += `\nThank you!`;
    setWaModal({ open: true, id: inv.id, phone, message });
  };

  const handleCancel = async (id) => {
    const key = `cancel:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      await api.post(`/api/invoices/${id}/status`, { status: 'CANCELLED' });
      setToast('Invoice cancelled');
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setToast('Failed to cancel');
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };
  const openPayModal = (inv) => {
    setPayModal({ open: true, id: inv.id });
  };
  const closePayModal = () => setPayModal({ open: false, id: null });
  const openPaymentInfo = (inv) => setPayInfo({ open: true, inv });
  const closePaymentInfo = () => setPayInfo({ open: false, inv: null });

  const openPdfPreview = async (inv) => {
    try {
      setPdfPreview({ open: true, url: '', label: inv.number || '', kind: 'Invoice', loading: true, invoiceId: inv.id });
      const { data } = await api.get(`/api/invoices/${inv.id}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) {
      console.error('Failed to load PDF', e);
      setToast('Failed to load PDF');
      setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false, invoiceId: null });
    }
  };
  const closePdfPreview = () => setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false, invoiceId: null });
  const openSourceQuotePreview = async (inv) => {
    try {
      setPdfPreview({ open: true, url: '', label: '', kind: 'Quote', loading: true });
      const { data } = await api.get(`/api/invoices/${inv.id}/quote-pdf-url`);
      const url = data?.url;
      const label = data?.quoteNumber || '';
      if (!url) throw new Error('No URL returned');
      setPdfPreview((p) => ({ ...p, url, label, loading: false }));
    } catch (e) {
      console.error('Failed to load Quote PDF', e);
      setToast('Failed to load Quote');
      setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false });
    }
  };
  const openQuoteNumberPreview = async (quoteId, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || '', kind: 'Quote', loading: true });
      const { data } = await api.get(`/api/quotes/${quoteId}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) {
      console.error('Failed to load Quote PDF', e);
      setToast('Failed to load Quote');
      setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false });
    }
  };

  const extractProformaNumberFromNotes = (notes) => {
    try {
      const text = String(notes || '');
      // e.g. "Converted from Proforma PF/24-25/000123"
      const m = text.match(/Converted\s+from\s+Proforma\s+([^\s]+)/i);
      return m?.[1] || null;
    } catch (_) { return null; }
  };

  const openProformaPdfById = async (id, label) => {
    try {
      if (!id) throw new Error('Missing proforma id');
      setPdfPreview({ open: true, url: '', label: label || '', kind: 'Proforma', loading: true });
      const { data } = await api.get(`/api/proformas/${id}/pdf-url`);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      setPdfPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) {
      console.error('Failed to load Proforma PDF', e);
      setToast('Failed to load Proforma');
      setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false });
    }
  };

  const openRecurringProformaFromInvoice = async (inv) => {
    try {
      const proformaNumber = extractProformaNumberFromNotes(inv?.notes);
      if (!proformaNumber) {
        setToast('Proforma number not found for this invoice');
        return;
      }
      const { data } = await api.get('/api/proformas', { params: { number: proformaNumber, page: 1, pageSize: 10 } });
      const list = Array.isArray(data?.data) ? data.data : [];
      const match = list.find((p) => String(p?.number || '') === String(proformaNumber)) || list[0];
      if (!match?.id) {
        setToast('Proforma not found');
        return;
      }
      await openProformaPdfById(match.id, match.number || proformaNumber);
    } catch (e) {
      console.error(e);
      setToast('Failed to open Proforma');
    }
  };
  const openCustomerDetail = (inv) => {
    try {
      const id = inv?.customerSnapshot?.id || inv?.customerId || null;
      if (!id) return;
      const customer = inv?.customerSnapshot ? { id, ...inv.customerSnapshot } : { id, name: inv?.customerSnapshot?.name || '' };
      setCustomerDrawer({ open: true, id, customer });
    } catch (_) {}
  };
  const closeCustomerDrawer = () => setCustomerDrawer({ open: false, id: null, customer: null });

  const fetchCompanyForPrint = async () => {
    if (!company?.id) return {};
    try {
      const { data: compRes } = await api.get('/api/companies/' + company.id);
      const comp = compRes?.company || compRes;
      let enriched = comp || {};
      try {
        const { data: gstStatus } = await api.get(`/api/companies/${company.id}/gst-status`);
        enriched = {
          ...enriched,
          gstin: gstStatus.gstin ?? comp?.gstin,
          stateCode: gstStatus.stateCode ?? comp?.stateCode,
          hasGSTIN: !!gstStatus.hasGSTIN
        };
      } catch (_) {
        // ignore
      }
      return enriched;
    } catch (e) {
      return {};
    }
  };

  const openPosBill = async (inv) => {
    try {
      const [{ data: invRes }, companyData] = await Promise.all([
        api.get(`/api/invoices/${inv.id}`),
        fetchCompanyForPrint()
      ]);
      const detail = invRes?.invoice || invRes || {};
      const linesRaw = Array.isArray(detail.items) ? detail.items : (Array.isArray(detail.lineItems) ? detail.lineItems : []);
      const items = (linesRaw || []).map((l) => ({
        name: l.name || l.itemSnapshot?.name || l.itemName || l.description || 'Item',
        qty: Number(l.quantity ?? l.qty ?? 0),
        price: Number(l.price ?? l.rate ?? l.unitPrice ?? 0),
        discountAmount: Number(l.discountAmount ?? 0),
        discountPercent: Number(l.discountPercent ?? 0)
      }));

      // Use header totals from the invoice so POS bill matches invoice/PDF (source of truth).
      const taxTotals = {
        taxable: Number(detail.taxableValue || 0),
        cgst: Number(detail.cgstAmount || 0),
        sgst: Number(detail.sgstAmount || 0),
        igst: Number(detail.igstAmount || 0),
        cess: Number(detail.cessAmount || 0),
        grand: Number(detail.total || 0),
        supplyType: String(detail.supplyType || '').toUpperCase() || (Number(detail.igstAmount || 0) > 0 ? 'INTER' : 'INTRA')
      };

      // Option B receipt display: show item discounts and doc discount separately.
      const grossTotal = items.reduce((acc, it) => acc + (Number(it.qty || 0) * Number(it.price || 0)), 0);
      const lineDiscountTotal = items.reduce((acc, it) => {
        const gross = Number(it.qty || 0) * Number(it.price || 0);
        const discAmt = Math.max(0, Number(it.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        return acc + Math.min(gross, discAmt + discFromPct);
      }, 0);
      const netBeforeDocDiscount = items.reduce((acc, it) => {
        const gross = Number(it.qty || 0) * Number(it.price || 0);
        const discAmt = Math.max(0, Number(it.discountAmount || 0));
        const discPct = Math.max(0, Math.min(100, Number(it.discountPercent || 0)));
        const discFromPct = (discPct / 100) * gross;
        const lineDisc = Math.min(gross, discAmt + discFromPct);
        return acc + Math.max(0, gross - lineDisc);
      }, 0);
      const invDiscAmt = Math.max(0, Number(detail.discountAmount || 0));
      const invDiscPct = Math.max(0, Math.min(100, Number(detail.discountPercent || 0)));
      const docDisc = invDiscPct > 0 ? +((netBeforeDocDiscount * invDiscPct) / 100).toFixed(2) : invDiscAmt;
      const discountTotal = +(Number(lineDiscountTotal || 0) + Number(docDisc || 0)).toFixed(2);
      const snapshot = {
        company: companyData || {},
        customer: detail.customerSnapshot || inv.customerSnapshot || {},
        items,
        taxTotals,
        printedAt: new Date().toISOString(),
        invoiceDate: detail.date || '',
        posStateCode: detail.posStateCode || '',
        paymentDateTime: detail.paymentDate || '',
        paymentMethod: detail.paymentMethod || '',
        paymentReference: detail.paymentReference || '',
        billedBy: (() => {
          const u = detail.createdByUser;
          if (u?.firstName || u?.lastName) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
          if (u?.email) return u.email;
          if (detail.createdBy) return `User #${detail.createdBy}`;
          return '';
        })(),
        grossTotal: +grossTotal.toFixed(2),
        lineDiscountTotal: +lineDiscountTotal.toFixed(2),
        docDiscountAmount: +docDisc.toFixed(2),
        discountAmount: discountTotal
      };
      setBillPreview({
        open: true,
        invoiceId: inv.id,
        invoiceNumber: detail.number || inv.number || '',
        snapshot
      });
    } catch (e) {
      console.error('Failed to open POS bill', e);
      setToast('Failed to open POS bill');
    }
  };

  const handleSubmitMarkPaid = async (paymentData) => {
    if (!payModal.id) return;
    try {
      setActionLoading((s) => ({ ...s, [`paid:${payModal.id}`]: true }));
      await api.post(`/api/invoices/${payModal.id}/mark-paid`, paymentData);
      setToast('Invoice marked as paid');
      closePayModal();
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setToast('Failed to mark as paid');
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[`paid:${payModal.id}`]; return n; });
    }
  };

  const isLastInvoiceInSeries = (inv) => {
    const seq = Number(seriesState.currentSeq || 0);
    const prefix = seriesState.prefix || '';
    if (!inv?.number || !prefix || seq <= 0) return false;
    const expected = `${prefix}${String(seq).padStart(6, '0')}${seriesState.suffix || ''}`;
    return String(inv.number) === expected;
  };

  const canDeleteInvoice = (inv) => !hasInvoicePayment(inv) && isLastInvoiceInSeries(inv);

  const handleDeleteInvoice = async (inv) => {
    if (!inv?.id || !canDeleteInvoice(inv)) return;
    const label = inv.number || inv.id;
    if (!window.confirm(`Delete invoice ${label}? Only the last unpaid invoice can be deleted. This cannot be undone.`)) return;
    const key = `delete:${inv.id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      await api.delete(`/api/invoices/${inv.id}`);
      setToast(`Invoice ${label} deleted`);
      setSeriesState((s) => ({ ...s, currentSeq: Math.max(0, Number(s.currentSeq || 0) - 1) }));
      fetchInvoices();
    } catch (e) {
      console.error(e);
      setToast(e?.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const closeWhatsAppModal = () => setWaModal({ open: false, id: null, phone: '', message: '' });
  const handleSendWhatsApp = () => {
    const { id, phone, message } = waModal;
    if (!id) return;
    const key = `wa:${id}`;
    setActionLoading((s) => ({ ...s, [key]: true }));
    try {
      const digits = (phone || '').replace(/\D/g, '');
      const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank', 'noopener');
      setToast('Opening WhatsApp…');
    } finally {
      setActionLoading((s) => { const n = { ...s }; delete n[key]; return n; });
      closeWhatsAppModal();
    }
  };

  const StatusBadge = ({ status }) => {
    if (!status) return <span style={{ color: colors.stone400 }}>-</span>;
    const map = {
      DRAFT: matte.blue,
      APPROVED: matte.teal,
      SENT: matte.purple,
      PAID: matte.green,
      OVERDUE: matte.red,
      CANCELLED: matte.rust
    };
    const dotColor = map[String(status || '').toUpperCase()] || matte.grey;
    return (
      <span style={styles.statusWithDot}>
        <span style={styles.statusDot(dotColor)} />
        {status}
      </span>
    );
  };

  return (
    <div className="page-content">
      <style>{billingTableCSS()}</style>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Invoices</h1>
            <p style={styles.subtitle}>{total} total invoices</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          <HeaderDateFilters value={dateRange} onChange={handleDateChange} />
          <button style={styles.btnSecondary} onClick={() => setFormatModal(true)}>
            Set Number Format
          </button>
          <button
            style={styles.btnSecondary}
            onClick={() => setExportModal(true)}
            title="Export invoices"
          >
            <FiDownload size={14} /> Export
          </button>
          <button
            style={styles.btnSecondary}
            onClick={() => setNewInvModal(true)}
          >
            <FiPlus size={14} /> New Invoice
          </button>
        </div>
      </div>

      {/* Stats Section */}
      {showStats && (
        <div style={{ ...styles.card, background: colors.stone800, border: `1px solid ${colors.stone700}`, marginBottom: 12 }}>
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Overview</span>
              <button style={{ ...styles.btnIcon, color: colors.stone400 }} onClick={() => setShowStats(false)}><FiEyeOff size={14} /></button>
            </div>
          {(() => {
            const totalCount = Number(stats.totalCount || 0);
            const paidCount = Number(stats.paidCount || 0);
            const paidAmount = Number(stats.paidAmount || 0);
            const totalAmount = Number(stats.totalAmount || 0);
            const pendingAmount = Math.max(0, totalAmount - paidAmount);
            const cgstAmount = Number(stats.cgstAmount || 0);
            const sgstAmount = Number(stats.sgstAmount || 0);
            const igstAmount = Number(stats.igstAmount || 0);
            const cessAmount = Number(stats.cessAmount || 0);
            const gstAmount = cgstAmount + sgstAmount + igstAmount;
            const totalTaxAmount = gstAmount + cessAmount;
            const hasTaxBreakup = totalTaxAmount > 0;

            const serverByStatus = (stats && stats.byStatus) ? stats.byStatus : null;
            const statusVals = {
              DRAFT: Number(serverByStatus?.DRAFT ?? localStatusCounts.DRAFT ?? 0),
              APPROVED: Number(serverByStatus?.APPROVED ?? localStatusCounts.APPROVED ?? 0),
              SENT: Number(serverByStatus?.SENT ?? localStatusCounts.SENT ?? 0),
              OVERDUE: Number(serverByStatus?.OVERDUE ?? localStatusCounts.OVERDUE ?? 0),
              PAID: Number(serverByStatus?.PAID ?? paidCount ?? localStatusCounts.PAID ?? 0),
              CANCELLED: Number(serverByStatus?.CANCELLED ?? localStatusCounts.CANCELLED ?? 0),
              OTHER: Number(serverByStatus?.OTHER ?? localStatusCounts.OTHER ?? 0)
            };
            const showCancelled = statusVals.CANCELLED > 0;
            const showOther = statusVals.OTHER > 0;

            const Card = ({ title, subtitle, children }) => (
              <div style={styles.statCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <div style={styles.statTitle}>{title}</div>
                  {subtitle && <div style={{ fontSize: 10, color: colors.stone400 }}>{subtitle}</div>}
                </div>
                <div style={{ marginTop: 10 }}>{children}</div>
              </div>
            );

            const SegBar = ({ segments }) => {
              const tot = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
              return (
                <div style={styles.segBar}>
                  {segments.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={s.onClick}
                      title={s.title}
                      style={{
                        width: `${(Number(s.value || 0) / tot) * 100}%`,
                        background: s.color,
                        border: 'none',
                        padding: 0,
                        cursor: s.onClick ? 'pointer' : 'default'
                      }}
                    />
                  ))}
                </div>
              );
            };

            const Legend = ({ items }) => (
              <div style={styles.legendGrid}>
                {items.map((it, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={it.onClick}
                    style={{
                      ...styles.legendItem,
                      ...(it.active ? styles.legendItemActive : {})
                    }}
                    title={it.title}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color }} />
                      {it.label}
                    </span>
                    <span style={{ fontWeight: 600 }}>{it.value}</span>
                  </button>
                ))}
              </div>
            );

              const isStatusActive = (s) => {
                if (s === 'ALL') return !filters.status;
                return String(filters.status || '').toUpperCase() === s;
              };
              const applyStatusFilter = (s) => {
                setFilters((f) => ({ ...f, status: s === 'ALL' ? '' : s }));
                setPage(1);
              };

              const isCreatedFromActive = (src) => String(filters.createdFrom || '').toUpperCase() === String(src || '').toUpperCase();
              const applyCreatedFromFilter = (src) => {
                const next = String(src || '').toUpperCase();
                setFilters((f) => {
                  const cur = String(f.createdFrom || '').toUpperCase();
                  // Toggle off when clicking the active source (or explicitly clearing)
                  if (!next || next === 'ALL' || cur === next) return { ...f, createdFrom: '' };
                  return { ...f, createdFrom: next };
                });
                setPage(1);
              };

              const serverByCreatedFrom = (stats && stats.byCreatedFrom) ? stats.byCreatedFrom : null;
              const createdFromVals = {
                DIRECT: Number(serverByCreatedFrom?.DIRECT ?? localCreatedFromCounts.DIRECT ?? 0),
                POS: Number(serverByCreatedFrom?.POS ?? localCreatedFromCounts.POS ?? 0),
                CONVERTED_QUOTE: Number(serverByCreatedFrom?.CONVERTED_QUOTE ?? localCreatedFromCounts.CONVERTED_QUOTE ?? 0),
                CUSTOMER_ORDER: Number(serverByCreatedFrom?.CUSTOMER_ORDER ?? localCreatedFromCounts.CUSTOMER_ORDER ?? 0),
                QUOTE_ADVANCE: Number(serverByCreatedFrom?.QUOTE_ADVANCE ?? localCreatedFromCounts.QUOTE_ADVANCE ?? 0),
                RECURRING_PROFORMA: Number(serverByCreatedFrom?.RECURRING_PROFORMA ?? localCreatedFromCounts.RECURRING_PROFORMA ?? 0),
                OTHER: Number(serverByCreatedFrom?.OTHER ?? localCreatedFromCounts.OTHER ?? 0)
              };

              const createdFromLabel = (k) => {
                const key = String(k || '').toUpperCase();
                const map = {
                  DIRECT: 'Direct',
                  POS: 'POS',
                  CONVERTED_QUOTE: 'Quote',
                  CUSTOMER_ORDER: 'Customer Order',
                  QUOTE_ADVANCE: 'Quote Advance',
                  RECURRING_PROFORMA: 'Recurring Proforma',
                  OTHER: 'Other'
                };
                return map[key] || String(k || '').replace(/_/g, ' ');
              };

              const serverByCreatedFromAmount = (stats && stats.byCreatedFromAmount) ? stats.byCreatedFromAmount : null;
              const createdFromAmountVals = {
                DIRECT: Number(serverByCreatedFromAmount?.DIRECT ?? localCreatedFromAmounts.DIRECT ?? 0),
                POS: Number(serverByCreatedFromAmount?.POS ?? localCreatedFromAmounts.POS ?? 0),
                CONVERTED_QUOTE: Number(serverByCreatedFromAmount?.CONVERTED_QUOTE ?? localCreatedFromAmounts.CONVERTED_QUOTE ?? 0),
                CUSTOMER_ORDER: Number(serverByCreatedFromAmount?.CUSTOMER_ORDER ?? localCreatedFromAmounts.CUSTOMER_ORDER ?? 0),
                QUOTE_ADVANCE: Number(serverByCreatedFromAmount?.QUOTE_ADVANCE ?? localCreatedFromAmounts.QUOTE_ADVANCE ?? 0),
                RECURRING_PROFORMA: Number(serverByCreatedFromAmount?.RECURRING_PROFORMA ?? localCreatedFromAmounts.RECURRING_PROFORMA ?? 0),
                OTHER: Number(serverByCreatedFromAmount?.OTHER ?? localCreatedFromAmounts.OTHER ?? 0)
              };

              const serverByCreatedFromPaidAmount = (stats && stats.byCreatedFromPaidAmount) ? stats.byCreatedFromPaidAmount : null;
              const createdFromPaidAmountVals = {
                DIRECT: Number(serverByCreatedFromPaidAmount?.DIRECT ?? localCreatedFromPaidAmounts.DIRECT ?? 0),
                POS: Number(serverByCreatedFromPaidAmount?.POS ?? localCreatedFromPaidAmounts.POS ?? 0),
                CONVERTED_QUOTE: Number(serverByCreatedFromPaidAmount?.CONVERTED_QUOTE ?? localCreatedFromPaidAmounts.CONVERTED_QUOTE ?? 0),
                CUSTOMER_ORDER: Number(serverByCreatedFromPaidAmount?.CUSTOMER_ORDER ?? localCreatedFromPaidAmounts.CUSTOMER_ORDER ?? 0),
                QUOTE_ADVANCE: Number(serverByCreatedFromPaidAmount?.QUOTE_ADVANCE ?? localCreatedFromPaidAmounts.QUOTE_ADVANCE ?? 0),
                RECURRING_PROFORMA: Number(serverByCreatedFromPaidAmount?.RECURRING_PROFORMA ?? localCreatedFromPaidAmounts.RECURRING_PROFORMA ?? 0),
                OTHER: Number(serverByCreatedFromPaidAmount?.OTHER ?? localCreatedFromPaidAmounts.OTHER ?? 0)
              };

              return (
                <div style={styles.statsGrid}>
                  <Card title="Status" subtitle="Click to filter">
                    <SegBar segments={[
                      { value: statusVals.PAID, color: matte.green, title: 'Paid', onClick: () => applyStatusFilter('PAID') },
                      { value: statusVals.OVERDUE, color: matte.red, title: 'Overdue', onClick: () => applyStatusFilter('OVERDUE') },
                      { value: statusVals.SENT, color: matte.purple, title: 'Sent', onClick: () => applyStatusFilter('SENT') },
                      { value: statusVals.APPROVED, color: matte.teal, title: 'Approved', onClick: () => applyStatusFilter('APPROVED') },
                      { value: statusVals.DRAFT, color: matte.blue, title: 'Draft', onClick: () => applyStatusFilter('DRAFT') },
                      ...(showCancelled ? [{ value: statusVals.CANCELLED, color: matte.rust, title: 'Cancelled', onClick: () => applyStatusFilter('CANCELLED') }] : []),
                      ...(showOther ? [{ value: statusVals.OTHER, color: matte.indigo, title: 'Other', onClick: () => applyStatusFilter('ALL') }] : [])
                    ]} />
                    <Legend items={[
                      { label: 'Paid', value: paidCount, color: matte.green, onClick: () => applyStatusFilter('PAID'), active: isStatusActive('PAID'), title: 'Filter: Paid' },
                      { label: 'Overdue', value: statusVals.OVERDUE, color: matte.red, onClick: () => applyStatusFilter('OVERDUE'), active: isStatusActive('OVERDUE'), title: 'Filter: Overdue' },
                      { label: 'Sent', value: statusVals.SENT, color: matte.purple, onClick: () => applyStatusFilter('SENT'), active: isStatusActive('SENT'), title: 'Filter: Sent' },
                      { label: 'Approved', value: statusVals.APPROVED, color: matte.teal, onClick: () => applyStatusFilter('APPROVED'), active: isStatusActive('APPROVED'), title: 'Filter: Approved' },
                      { label: 'Draft', value: statusVals.DRAFT, color: matte.blue, onClick: () => applyStatusFilter('DRAFT'), active: isStatusActive('DRAFT'), title: 'Filter: Draft' },
                      ...(showCancelled ? [{ label: 'Cancelled', value: statusVals.CANCELLED, color: matte.rust, onClick: () => applyStatusFilter('CANCELLED'), active: isStatusActive('CANCELLED'), title: 'Filter: Cancelled' }] : []),
                      { label: 'All', value: totalCount, color: matte.grey, onClick: () => applyStatusFilter('ALL'), active: isStatusActive('ALL'), title: 'Clear status filter' }
                    ]} />
                  </Card>

                  <Card title="Amounts" subtitle="INR">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fff', fontSize: 12 }}>
                        <span>Total</span><span style={{ fontWeight: 600 }}>{formatINR(totalAmount)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: matte.green, fontSize: 12 }}>
                        <span>Paid</span><span style={{ fontWeight: 600 }}>{formatINR(paidAmount)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: matte.blue, fontSize: 12 }}>
                        <span>Pending</span><span style={{ fontWeight: 600 }}>{formatINR(pendingAmount)}</span>
                      </div>
                      {hasTaxBreakup && (
                        <>
                          <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '2px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
                            <span>Total Tax</span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatINR(totalTaxAmount)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                            <span>GST</span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatINR(gstAmount)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                            <span>Cess</span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatINR(cessAmount)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <SegBar segments={[
                      { value: paidAmount, color: matte.green, title: 'Paid amount', onClick: null },
                      { value: pendingAmount, color: matte.blue, title: 'Pending amount', onClick: null }
                    ]} />
                  </Card>

                  <Card title="Created From" subtitle="Received / Total • Click to filter">
                    {(() => {
                      const order = ['DIRECT', 'POS', 'CONVERTED_QUOTE', 'CUSTOMER_ORDER', 'QUOTE_ADVANCE', 'RECURRING_PROFORMA', 'OTHER'];
                      const sourceColors = {
                        DIRECT: matte.blue,
                        POS: matte.amber,
                        CONVERTED_QUOTE: matte.purple,
                        CUSTOMER_ORDER: matte.teal,
                        QUOTE_ADVANCE: matte.green,
                        RECURRING_PROFORMA: matte.rust,
                        OTHER: matte.indigo
                      };
                      const nonZero = order.filter(k =>
                        Number(createdFromVals[k] || 0) > 0 ||
                        Number(createdFromAmountVals[k] || 0) > 0 ||
                        Number(createdFromPaidAmountVals[k] || 0) > 0
                      );
                      const showKeys = nonZero.length ? nonZero : ['DIRECT', 'OTHER'];
                      return (
                        <div>
                          <SegBar segments={showKeys.map((k) => ({
                            value: Number(createdFromAmountVals[k] || 0),
                            color: sourceColors[k] || colors.stone300,
                            title: `${createdFromLabel(k)}: ${formatINR(Number(createdFromPaidAmountVals[k] || 0))} / ${formatINR(Number(createdFromAmountVals[k] || 0))} • ${Number(createdFromVals[k] || 0)} invoices (Received / Total)`,
                            onClick: () => applyCreatedFromFilter(k)
                          }))} />
                          {(() => {
                            const rows = showKeys.map((k) => ({
                              key: k,
                              label: createdFromLabel(k),
                              color: sourceColors[k] || colors.stone300,
                              value: `${formatINR(Number(createdFromPaidAmountVals[k] || 0))} / ${formatINR(Number(createdFromAmountVals[k] || 0))}`,
                              meta: `${Number(createdFromVals[k] || 0)}`,
                              onClick: () => applyCreatedFromFilter(k),
                              active: isCreatedFromActive(k),
                              title: isCreatedFromActive(k) ? `Clear filter: ${createdFromLabel(k)}` : `Filter: ${createdFromLabel(k)}`
                            })).concat([{
                              key: 'ALL',
                              label: 'All',
                              color: matte.grey,
                              value: `${formatINR(Number(paidAmount || 0))} / ${formatINR(Number(totalAmount || 0))}`,
                              meta: `${Number(totalCount || 0)}`,
                              onClick: () => applyCreatedFromFilter('ALL'),
                              active: !filters.createdFrom,
                              title: 'Clear created-from filter'
                            }]);

                            return (
                              <div style={styles.legendGrid}>
                                {rows.map((r) => (
                                  <button
                                    key={r.key}
                                    type="button"
                                    onClick={r.onClick}
                                    title={r.title}
                                    style={{
                                      ...styles.legendItem,
                                      ...(r.active ? styles.legendItemActive : {}),
                                      display: 'grid',
                                      gap: 2,
                                      textAlign: 'left'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color }} />
                                        {r.label}
                                      </span>
                                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{r.meta}</span>
                                    </div>
                                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}>
                                      {r.value}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </Card>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {!showStats && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button style={styles.btnSecondary} onClick={() => setShowStats(true)}>
            <FiChevronDown size={14} /> Show Stats
          </button>
        </div>
      )}

      {/* Quick Search / Filters */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={14} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Invoice number..."
                value={filters.number}
                onChange={(e) => { setFilters((f) => ({ ...f, number: e.target.value })); setPage(1); }}
              />
            </div>
            <select
              style={styles.select}
              value={filters.customerId}
              onFocus={() => { if (!customersLoading && customers.length === 0) fetchCustomers(); }}
              onClick={() => { if (!customersLoading && customers.length === 0) fetchCustomers(); }}
              onChange={(e) => { setFilters((f) => ({ ...f, customerId: e.target.value })); setPage(1); }}
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              style={styles.btnSecondary}
              onClick={() => { setFilters((f) => ({ ...f, customerId: '', number: '', status: '', createdFrom: '' })); setPage(1); }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {!!seriesState.error && (
        <div style={{ marginBottom: 12, color: colors.rose700, background: colors.rose50, padding: '10px 14px', borderRadius: 10, fontSize: 12 }}>
          {seriesState.error}
        </div>
      )}

      {/* Table Card */}
      <div style={styles.card}>
       {/*  <div style={styles.cardHeader}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.stone700 }}>All Invoices</span>
          <span style={{ fontSize: 11, color: colors.stone400 }}>{total} results</span>
        </div> */}
        <div style={styles.tableWrap}>
          <table className="billing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Number</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Due Date</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Created From</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="billing-row">
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    <button
                      style={styles.linkBtn}
                      onClick={() => openPdfPreview(inv)}
                      title="Preview PDF"
                    >
                      {inv.number}
                    </button>
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{inv.date}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{inv.dueDate || <span style={{ color: colors.stone400 }}>-</span>}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {(inv.customerSnapshot?.id || inv.customerId) ? (
                      <button
                        style={styles.linkBtn}
                        title="Open Customer"
                        onClick={() => openCustomerDetail(inv)}
                      >
                        {inv.customerSnapshot?.name || `Customer ${inv.customerSnapshot?.id || inv.customerId}`}
                      </button>
                    ) : (inv.customerSnapshot?.name || <span style={{ color: colors.stone400 }}>-</span>)}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {inv.createdFrom === 'CONVERTED_QUOTE' ? (
                        <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {Array.isArray(inv.sourceQuotes) && inv.sourceQuotes.length ? (
                            inv.sourceQuotes.map((q) => (
                              <button
                                key={q.id}
                                style={{ ...styles.badge, background: colors.accent50, color: colors.accent700, cursor: 'pointer', border: 'none' }}
                                onClick={() => openQuoteNumberPreview(q.id, q.number)}
                                title={`Quote ${q.number}`}
                              >
                                {q.number}
                              </button>
                            ))
                          ) : (
                            <span style={{ color: colors.stone500, fontSize: 11 }}>
                              {(Array.isArray(inv.sourceQuoteNumbers) && inv.sourceQuoteNumbers.length) ? inv.sourceQuoteNumbers.join(', ') : 'Quote'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <>
                          {inv.createdFrom === 'RECURRING_PROFORMA' ? (
                            <button
                              type="button"
                              style={styles.linkBtn}
                              onClick={() => openRecurringProformaFromInvoice(inv)}
                              title="Open source Proforma"
                            >
                              {extractProformaNumberFromNotes(inv?.notes) || 'Recurring Proforma'}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: colors.stone500 }}>
                              {(inv.createdFrom ? String(inv.createdFrom).replace(/_/g, ' ') : '-')}
                            </span>
                          )}
                          {inv.createdFrom === 'POS' && (
                            <button style={styles.btnIcon} title="Print Receipt" onClick={() => openPosBill(inv)}>
                              <FiPrinter size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontVariantNumeric: 'tabular-nums' }}>
                    {formatINR(inv.total)}
                  </td>
                  <td style={styles.td}>
                    {inv.status === 'PAID' ? (
                      <button
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => openPaymentInfo(inv)}
                        title="View Payment Details"
                      >
                        <StatusBadge status={inv.status} />
                      </button>
                    ) : (
                      <StatusBadge status={inv.status} />
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      {isInvoiceEditable(inv) && (
                        <Link to={`/invoice/${inv.id}/edit`} style={styles.btnIcon} title="Edit invoice">
                          <FiEdit2 size={14} />
                        </Link>
                      )}
                      {canDeleteInvoice(inv) && (
                        <button
                          style={{ ...styles.btnIcon, color: colors.rose600 }}
                          onClick={() => handleDeleteInvoice(inv)}
                          disabled={!!actionLoading[`delete:${inv.id}`]}
                          title="Delete last unpaid invoice"
                        >
                          {actionLoading[`delete:${inv.id}`] ? <span className="button-loading-spinner" /> : <FiTrash2 size={14} />}
                        </button>
                      )}
                      <button
                        style={styles.btnIcon}
                        onClick={() => handleSendEmail(inv.id)}
                        disabled={!!actionLoading[`email:${inv.id}`]}
                        title="Send Email"
                      >
                        {actionLoading[`email:${inv.id}`] ? <span className="button-loading-spinner" /> : <FiMail size={14} />}
                      </button>
                      <button
                        style={styles.btnIcon}
                        onClick={() => openWhatsAppModal(inv)}
                        disabled={!!actionLoading[`wa:${inv.id}`]}
                        title="Send WhatsApp"
                      >
                        <FiMessageCircle size={14} />
                      </button>
                      <button
                        style={styles.btnIcon}
                        onClick={() => handleRegeneratePdf(inv)}
                        disabled={!!actionLoading[`regen-pdf:${inv.id}`]}
                        title="Regenerate PDF"
                      >
                        {actionLoading[`regen-pdf:${inv.id}`] ? <span className="button-loading-spinner" /> : <FiRefreshCw size={14} />}
                      </button>
                      {!isInvoicePaid(inv) && inv.status !== 'CANCELLED' && (
                        <button
                          style={{ ...styles.btnIcon, color: colors.emerald600 }}
                          title="Mark Paid"
                          onClick={() => openPayModal(inv)}
                          disabled={!!actionLoading[`paid:${inv.id}`]}
                        >
                          {actionLoading[`paid:${inv.id}`] ? <span className="button-loading-spinner" /> : <FiCreditCard size={14} />}
                        </button>
                      )}
                      {inv.status === 'PAID' && (
                        <span style={{ ...styles.badge, color: colors.stone800 }}>
                          <FiCheckCircle size={10} /> Paid
                        </span>
                      )}
                      {inv.status === 'CANCELLED' && (
                        <span style={{ ...styles.badge, color: colors.stone800 }}>
                          Cancelled
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!loading && invoices.length === 0) && (
                <tr>
                  <td colSpan={8} style={{ ...styles.td, padding: 32, textAlign: 'center', color: colors.stone400 }}>
                    No invoices found for the selected filters.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={8} style={{ ...styles.td, padding: 32, textAlign: 'center' }}>
                    <div className="loading-spinner" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${colors.stone100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: colors.stone500 }}>
            Showing {Math.min((page - 1) * pageSize + 1, total)} - {Math.min(page * pageSize, total)} of {total}
          </span>
          <div style={styles.pagination}>
            <button
              style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }}
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <FiChevronLeft size={14} />
            </button>
            <span style={{ fontSize: 12, color: colors.stone600, padding: '0 8px' }}>Page {page}</span>
            <button
              style={{ ...styles.pageBtn, opacity: page * pageSize >= total ? 0.5 : 1 }}
              disabled={page * pageSize >= total}
              onClick={() => setPage(p => p + 1)}
            >
              <FiChevronRight size={14} />
            </button>
            <select
              style={{ ...styles.select, width: 70, marginLeft: 8 }}
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* PDF Preview Drawer */}
      {pdfPreview.open && (
        <div style={styles.drawer}>
          <div style={styles.drawerContent}>
            <div style={styles.drawerHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>
                {pdfPreview.kind || 'Document'} {pdfPreview.label ? `- ${pdfPreview.label}` : ''}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {pdfPreview.kind === 'Invoice' && pdfPreview.invoiceId && (
                  <button
                    style={styles.btnSecondary}
                    onClick={() => handleRegeneratePdf(invoices.find((x) => x.id === pdfPreview.invoiceId) || { id: pdfPreview.invoiceId, number: pdfPreview.label })}
                    disabled={!!actionLoading[`regen-pdf:${pdfPreview.invoiceId}`]}
                  >
                    <FiRefreshCw size={14} /> Regenerate PDF
                  </button>
                )}
                {!!pdfPreview.url && (
                  <a style={styles.btnSecondary} href={pdfPreview.url} target="_blank" rel="noreferrer">
                    Open in new tab
                  </a>
                )}
                <button style={styles.btnSecondary} onClick={closePdfPreview}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="loading-spinner" />
                </div>
              ) : (
                <>
                  {!!pdfPreview.url ? (
                    <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
                  ) : (
                    <div style={{ padding: 24, color: colors.stone400 }}>No preview available</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Customer Drawer */}
      {customerDrawer.open && (
        <div style={{ ...styles.drawer }}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }}>
            <div style={styles.drawerHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Customer</h3>
              <button style={styles.btnSecondary} onClick={closeCustomerDrawer}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <CustomerDetail embeddedCustomerId={customerDrawer.id} embeddedCustomer={customerDrawer.customer} />
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {waModal.open && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 440 }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Send via WhatsApp</h3>
              <button style={styles.btnIcon} onClick={closeWhatsAppModal}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  style={styles.input}
                  value={waModal.phone}
                  onChange={(e) => setWaModal((m) => ({ ...m, phone: e.target.value }))}
                  placeholder="e.g. 9198xxxxxx"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Message</label>
                <textarea
                  style={{ ...styles.input, minHeight: 100, resize: 'vertical' }}
                  rows={4}
                  value={waModal.message}
                  onChange={(e) => setWaModal((m) => ({ ...m, message: e.target.value }))}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={closeWhatsAppModal}>Cancel</button>
              <button style={styles.btnSecondary} onClick={handleSendWhatsApp}>
                <FiMessageCircle size={14} /> Open WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {billPreview.open && (
        <div style={styles.modal}>
          <div className="pos-print-modal-content" style={{ ...styles.modalContent, maxWidth: 840, maxHeight: '92vh', overflow: 'auto' }}>
            <style>{`
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                body * { visibility: hidden !important; }
                .pos-print-area, .pos-print-area * { visibility: visible !important; }
                .no-print { display: none !important; }
                @page { margin: 4mm; }
                html, body { width: 100%; }
                .pos-print-modal-content {
                  max-height: none !important;
                  overflow: visible !important;
                  width: auto !important;
                }
                .pos-print-area { position: static !important; margin: 0 auto; display: block !important; }
                .pos-bill { width: 100% !important; max-width: 320px !important; font-size: 11px; color: #111827; box-sizing: border-box; }
                .pos-bill h1, .pos-bill h2, .pos-bill h3 { margin: 0; }
                .pos-bill hr { border: none; border-top: 1px dashed #9ca3af; margin: 4px 0; }
                .pos-bill table { width: 100%; border-collapse: collapse; }
                .pos-bill th, .pos-bill td { padding: 1.2mm 0; font-size: 10px; line-height: 1.15; }
                .pos-bill th { text-align: left; }
                .pos-bill td.qty, .pos-bill td.right { text-align: right; }
              }
              @media screen {
                .pos-print-area { display: block; }
              }
            `}</style>
            <div className="no-print" style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>
                Receipt {billPreview.invoiceNumber ? `- ${billPreview.invoiceNumber}` : ''}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.btnSecondary} onClick={() => window.print()}>
                  <FiPrinter size={14} /> Print
                </button>
                <button style={styles.btnSecondary} onClick={() => setBillPreview({ open: false, invoiceId: null, invoiceNumber: '', snapshot: null })}>
                  Close
                </button>
              </div>
            </div>
            {(() => {
              const snap = billPreview.snapshot || {};
              const money = (v) => {
                try { return new Intl.NumberFormat('en-IN', { style:'currency', currency:'INR' }).format(Number(v||0)); } catch { return v; }
              };
              const fmtDT = (iso) => {
                if (!iso) return '';
                try {
                  const d = new Date(iso);
                  if (Number.isNaN(d.getTime())) return String(iso);
                  return d.toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
                } catch { return String(iso); }
              };
              const companyName = (snap.company?.name || 'Company');
              const companyGstin = String(snap.company?.gstin || '').trim();
              const companyStateCode = snap.company?.stateCode || '';
              const companyState = getStateDisplayName(companyStateCode);
              const companyWebsite = (snap.company?.website || '');
              const companyPhone = (snap.company?.phone || '');
              const companyEmail = (snap.company?.email || '');
              const companyAddress = (() => {
                const a = snap.company?.address;
                if (!a) return '';
                if (typeof a === 'string') return a;
                if (typeof a === 'object') {
                  const parts = [a.addressLine1, a.addressLine2, a.city, a.state, a.pincode, a.zip, a.country].filter(Boolean);
                  if (parts.length) return parts.join(', ');
                  const generic = Object.values(a || {}).filter(v => typeof v === 'string').join(', ');
                  return generic;
                }
                return '';
              })();
              const customerName = snap.customer?.name || 'Customer';
              const customerLine = [snap.customer?.email, snap.customer?.phone].filter(Boolean).join(' • ');
              const customerGstin = String(snap.customer?.gstin || '').trim();
              const customerState = getStateDisplayName(snap.customer?.stateCode || '');
              const billedBy = snap.billedBy || '';
              const invoiceNumberInline = billPreview.invoiceNumber || '';
              const printedAt = snap.printedAt || '';
              const paidAt = snap.paymentDateTime || '';
              const method = snap.paymentMethod || '';
              const placeOfSale = getStateDisplayName(snap.posStateCode || companyStateCode || '');
              const companyMeta = [];
              if (companyGstin) companyMeta.push(`GSTIN: ${companyGstin}`);
              if (companyState) companyMeta.push(`State: ${companyState}`);
              const customerMeta = [];
              if (customerGstin) customerMeta.push(`GSTIN: ${customerGstin}`);
              if (customerState) customerMeta.push(`State: ${customerState}`);
              return (
                <>
                  <div className="pos-bill pos-print-area" style={{ margin:'0 auto', width:'100%', maxWidth:320 }}>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontWeight:600, fontSize:12 }}>Receipt</div>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 9, color:'#6b7280', display:'grid', gridTemplateColumns:'1fr 1fr', gap: 2 }}>
                      <div>Bill No: {invoiceNumberInline || '-'}</div>
                      <div style={{ textAlign:'right' }}>Printed: {printedAt ? fmtDT(printedAt) : '-'}</div>
                      <div>Paid at: {paidAt ? fmtDT(paidAt) : '-'}</div>
                      <div style={{ textAlign:'right' }}>Method: {method || '-'}</div>
                      <div>Place of Sale: {placeOfSale || '-'}</div>
                      <div style={{ textAlign:'right' }}>Billed by: {billedBy || '-'}</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:6, padding:6, marginTop:6, marginBottom:6, display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                      <div>
                        <div style={{ fontWeight:600 }}>{companyName}</div>
                        {companyMeta.length > 0 && <div style={{ color:'#6b7280', fontSize:10 }}>{companyMeta.join(' • ')}</div>}
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:600 }}>{customerName}</div>
                        {!!customerLine && <div style={{ color:'#6b7280', fontSize:9 }}>{customerLine}</div>}
                        {customerMeta.length > 0 && <div style={{ color:'#6b7280', fontSize:9 }}>{customerMeta.join(' • ')}</div>}
                      </div>
                    </div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontVariantNumeric:'tabular-nums' }}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="right" style={{ textAlign:'right' }}>Qty</th>
                          <th className="right" style={{ textAlign:'right' }}>Price</th>
                          <th className="right" style={{ textAlign:'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(snap.items || []).map((l, i) => (
                          <tr key={i}>
                            <td>{l.name}</td>
                            <td className="right" style={{ textAlign:'right' }}>{l.qty}</td>
                            <td className="right" style={{ textAlign:'right' }}>{money(l.price)}</td>
                            <td className="right" style={{ textAlign:'right' }}>{(() => {
                              const qty = Number(l.qty || 0);
                              const price = Number(l.price || 0);
                              const gross = qty * price;
                              const discAmt = Math.max(0, Number(l.discountAmount || 0));
                              const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
                              const discountFromPercent = (discPct / 100) * gross;
                              const net = Math.max(0, gross - discAmt - discountFromPercent);
                              return money(net);
                            })()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <hr />
                    <table style={{ width:'100%', borderCollapse:'collapse', fontVariantNumeric:'tabular-nums' }}>
                      <tbody>
                        {(() => {
                          const gross = Number(snap.grossTotal || 0);
                          const itemDisc = Number(snap.lineDiscountTotal || 0);
                          const docDisc = Number(snap.docDiscountAmount || snap.discountAmount || 0);
                          const subtotalAfterItems = Math.max(0, gross - itemDisc);
                          return (
                            <>
                              <tr><td>Total</td><td className="right" style={{ textAlign:'right' }}>{money(gross)}</td></tr>
                              <tr><td>Item Discounts</td><td className="right" style={{ textAlign:'right', color:'#ef4444', fontWeight:600 }}>-{money(itemDisc)}</td></tr>
                              <tr><td>Subtotal</td><td className="right" style={{ textAlign:'right' }}>{money(subtotalAfterItems)}</td></tr>
                              <tr><td>Doc Discount</td><td className="right" style={{ textAlign:'right', color:'#ef4444', fontWeight:600 }}>-{money(docDisc)}</td></tr>
                            </>
                          );
                        })()}
                        <tr><td>Taxable</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.taxable || 0)}</td></tr>
                        <tr><td>CGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.cgst || 0)}</td></tr>
                        <tr><td>SGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.sgst || 0)}</td></tr>
                        <tr><td>IGST</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.igst || 0)}</td></tr>
                        <tr><td>Cess</td><td className="right" style={{ textAlign:'right' }}>{money(snap.taxTotals?.cess || 0)}</td></tr>
                        <tr><td style={{ fontWeight:600 }}>Grand Total</td><td className="right" style={{ textAlign:'right', fontWeight:700 }}>{money(snap.taxTotals?.grand || 0)}</td></tr>
                      </tbody>
                    </table>
                    <hr />
                    <div style={{ textAlign:'center', fontSize:9, marginBottom:4 }}>Thank you!</div>
                    <div style={{ fontSize:9, color:'#6b7280', marginTop:4 }}>
                      {!!companyAddress && <div style={{ marginTop:2 }}>{companyAddress}</div>}
                      <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                        <span>{companyPhone || ''}</span>
                        <span>{companyEmail || ''}</span>
                        <span style={{ overflowWrap:'anywhere' }}>{companyWebsite || ''}</span>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      <PaymentModal
        isOpen={payModal.open}
        onClose={closePayModal}
        onSubmit={handleSubmitMarkPaid}
        title="Mark Invoice as Paid"
        submitText="Mark as Paid"
        submitLoadingText="Saving..."
        isSubmitting={!!actionLoading[`paid:${payModal.id}`]}
      />

      {/* Payment Details Modal */}
      {payInfo.open && payInfo.inv && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 560 }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Payment Details</h3>
              <button style={styles.btnIcon} onClick={closePaymentInfo}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Method</label>
                  <div style={{ fontSize: 13, color: colors.stone700 }}>{payInfo.inv.paymentMethod || '-'}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Reference</label>
                  <div style={{ fontSize: 13, color: colors.stone700 }}>{payInfo.inv.paymentReference || '-'}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Date</label>
                  <div style={{ fontSize: 13, color: colors.stone700 }}>{payInfo.inv.paymentDate ? new Date(payInfo.inv.paymentDate).toLocaleDateString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Amount</label>
                  <div style={{ fontSize: 13, color: colors.stone700, fontWeight: 600 }}>{formatINR(payInfo.inv.amountPaid || payInfo.inv.total)}</div>
                </div>
              </div>
              {!!payInfo.inv.paymentProofUrl && (
                <div style={{ ...styles.formGroup, marginTop: 8 }}>
                  <label style={styles.label}>Proof</label>
                  <a href={payInfo.inv.paymentProofUrl} target="_blank" rel="noreferrer">
                    <img
                      src={payInfo.inv.paymentProofUrl}
                      alt="Payment Proof"
                      style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: `1px solid ${colors.stone200}` }}
                    />
                  </a>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={closePaymentInfo}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Number Format Modal */}
      {formatModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 540 }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Set Invoice Number Format</h3>
              <button style={styles.btnIcon} onClick={() => setFormatModal(false)}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 12 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Prefix (before year)</label>
                  <input
                    style={styles.input}
                    value={seriesState.prefixPre}
                    onChange={(e) => {
                      let val = (e.target.value || '');
                      if (seriesState.fy) val = val.replaceAll(seriesState.fy, '');
                      const sep = seriesState.fySep || '/';
                      while (val.endsWith(sep)) { val = val.slice(0, -1); }
                      setSeriesState(s => ({ ...s, prefixPre: val }));
                    }}
                    placeholder="e.g., INV"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Fiscal Year</label>
                  <input
                    style={{ ...styles.input, color: colors.stone500, background: colors.stone100 }}
                    value={(seriesState.fy ? ((seriesState.fySep || '/') + seriesState.fy + (seriesState.fySep || '/')) : '-')}
                    readOnly
                    disabled
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Suffix</label>
                  <input
                    style={styles.input}
                    value={seriesState.suffix}
                    onChange={(e) => setSeriesState(s => ({ ...s, suffix: e.target.value }))}
                    placeholder="e.g., -A"
                  />
                </div>
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: colors.stone500, background: colors.stone50, padding: '10px 12px', borderRadius: 8 }}>
                <span style={{ fontWeight: 600, color: colors.stone600 }}>Example:</span>{' '}
                {seriesState.prefixPre}{seriesState.fySep}{seriesState.fy}{seriesState.fySep}
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{String((seriesState.currentSeq || 0) + 1).padStart(6, '0')}</span>
                {seriesState.suffix || ''}
              </div>
              {!!formatOptions.length && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12, color: colors.stone700 }}>Existing formats</div>
                  <div style={{ display: 'grid', gap: 8, maxHeight: 200, overflow: 'auto' }}>
                    {formatOptions.map((r) => {
                      const rowFy = (() => {
                        try {
                          if (r.fyStart && r.fyEnd) {
                            const y1 = String(new Date(r.fyStart).getUTCFullYear()).slice(-2);
                            const y2 = String(new Date(r.fyEnd).getUTCFullYear()).slice(-2);
                            return `${y1}-${y2}`;
                          }
                        } catch {}
                        return '';
                      })();
                      return (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${colors.stone200}`, borderRadius: 10, padding: '8px 10px', background: '#fff' }}>
                          <div style={{ fontSize: 12 }}>
                            <div>
                              <span style={{ fontWeight: 600 }}>Prefix:</span>{' '}
                              <span style={{ fontFamily: 'monospace' }}>{r.prefix}</span>
                              {r.suffix ? <span> • <span style={{ fontWeight: 600 }}>Suffix:</span> <span style={{ fontFamily: 'monospace' }}>{r.suffix}</span></span> : null}
                              {r.isActive ? <span style={{ ...styles.badge, marginLeft: 8, background: colors.emerald50, color: colors.emerald700 }}>In use</span> : null}
                              {selectedFormatId === r.id ? <span style={{ ...styles.badge, marginLeft: 8, background: colors.stone100, color: colors.stone600 }}>Selected</span> : null}
                            </div>
                            <div style={{ color: colors.stone500, marginTop: 2 }}>FY: {rowFy || '-'} • Last seq: {r.currentSeq}</div>
                          </div>
                          <button
                            style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                            onClick={() => applyExistingFormat(r)}
                            disabled={!!r.isActive || selectedFormatId === r.id || formatActivatingId === r.id}
                          >
                            {r.isActive ? 'In use' : (formatActivatingId === r.id ? 'Activating…' : (selectedFormatId === r.id ? 'Selected' : 'Use'))}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={() => setFormatModal(false)}>Cancel</button>
              <button style={styles.btnSecondary} disabled={seriesState.saving} onClick={saveSeriesFormat}>
                {seriesState.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 620 }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Export Invoices</h3>
              <button
                style={styles.btnIcon}
                onClick={() => {
                  if (exportState.running) return;
                  setExportModal(false);
                }}
                title={exportState.running ? 'Export running…' : 'Close'}
              >
                <FiX size={18} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ fontSize: 12, color: colors.stone500, marginBottom: 12, lineHeight: 1.6 }}>
                Exports use your current filters (date range, status, customer, source). Choose a format and scope below.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Scope</label>
                  <select
                    style={styles.select}
                    value={exportOptions.scope}
                    onChange={(e) => setExportOptions((o) => ({ ...o, scope: e.target.value }))}
                    disabled={exportState.running}
                  >
                    <option value="page">Current page ({invoices.length} rows)</option>
                    <option value="all">All results (may take time)</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Format</label>
                  <select
                    style={styles.select}
                    value={exportOptions.format}
                    onChange={(e) => setExportOptions((o) => ({ ...o, format: e.target.value }))}
                    disabled={exportState.running}
                  >
                    <option value="csv_summary">CSV (Summary)</option>
                    <option value="csv_detailed">CSV (Detailed - line items)</option>
                    <option value="xlsx_detailed">XLSX (Detailed - line items, frozen columns)</option>
                    <option value="json">JSON (Raw)</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includeCustomerContact}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includeCustomerContact: e.target.checked }))}
                    disabled={exportState.running}
                  />
                  Include customer contact
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includeItems}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includeItems: e.target.checked }))}
                    disabled={exportState.running || exportOptions.format === 'csv_detailed' || exportOptions.format === 'xlsx_detailed'}
                  />
                  Include items (fetch details)
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includePayment}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includePayment: e.target.checked }))}
                    disabled={exportState.running}
                  />
                  Include payment fields
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includeTaxBreakdown}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includeTaxBreakdown: e.target.checked }))}
                    disabled={exportState.running}
                  />
                  Include tax breakdown
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includeDiscountBreakdown}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includeDiscountBreakdown: e.target.checked }))}
                    disabled={exportState.running}
                  />
                  Include discount breakdown
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: colors.stone700 }}>
                  <input
                    type="checkbox"
                    checked={!!exportOptions.includeNotes}
                    onChange={(e) => setExportOptions((o) => ({ ...o, includeNotes: e.target.checked }))}
                    disabled={exportState.running}
                  />
                  Include notes
                </label>
              </div>

              {(exportOptions.includeItems && exportOptions.format !== 'csv_detailed') && (
                <div style={{ marginTop: 12, color: colors.amber700, background: colors.amber50, padding: '10px 12px', borderRadius: 10, fontSize: 12 }}>
                  Items export fetches each invoice to include item summaries. For large date ranges, prefer “Current page”.
                </div>
              )}
              {((exportOptions.includeTaxBreakdown || exportOptions.includeDiscountBreakdown) && exportOptions.format === 'csv_summary') && (
                <div style={{ marginTop: 12, color: colors.amber700, background: colors.amber50, padding: '10px 12px', borderRadius: 10, fontSize: 12 }}>
                  Tax/discount breakdown uses invoice <strong>detail</strong> to ensure the exported numbers match the invoice header totals.
                </div>
              )}

              {exportOptions.format === 'csv_detailed' && (
                <div style={{ marginTop: 12, color: colors.amber700, background: colors.amber50, padding: '10px 12px', borderRadius: 10, fontSize: 12 }}>
                  Detailed export fetches each invoice to include line items. For large date ranges, use “Current page” or expect a slower export.
                </div>
              )}
              {exportOptions.format === 'xlsx_detailed' && (
                <div style={{ marginTop: 12, color: colors.emerald700, background: colors.emerald50, padding: '10px 12px', borderRadius: 10, fontSize: 12 }}>
                  XLSX detailed export freezes the first two columns (Invoice # and Customer Name) plus the header row.
                </div>
              )}

              {exportState.running && (
                <div style={{ marginTop: 12, border: `1px solid ${colors.stone200}`, borderRadius: 12, padding: 12, background: colors.stone50 }}>
                  <div style={{ fontSize: 12, color: colors.stone700, fontWeight: 600 }}>{exportState.stage || 'Working…'}</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: colors.stone600 }}>
                    {exportState.total ? (
                      <span>
                        Progress: <strong>{exportState.processed}</strong> / <strong>{exportState.total}</strong>
                      </span>
                    ) : (
                      <span>Progress: <strong>{exportState.processed}</strong></span>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button style={styles.btnSecondary} onClick={cancelExport}>Cancel export</button>
                  </div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={() => {
                  if (exportState.running) return;
                  setExportModal(false);
                }}
                disabled={exportState.running}
              >
                Close
              </button>
              <button
                style={styles.btnSecondary}
                onClick={runExport}
                disabled={exportState.running}
                title={exportOptions.scope === 'all' ? 'Exports all matching results' : 'Exports current page'}
              >
                {exportState.running ? 'Exporting…' : 'Export'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Invoice Warning Modal */}
      {newInvModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: 520 }}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Before creating a new invoice</h3>
              <button style={styles.btnIcon} onClick={() => setNewInvModal(false)}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ margin: 0, color: colors.stone600, fontSize: 13, lineHeight: 1.6 }}>
                Create invoice only if payment is already received. Otherwise, create a quote.
                Unpaid invoices can be edited; only the last unpaid invoice in the series can be deleted.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnSecondary} onClick={() => setNewInvModal(false)}>Close</button>
              <button style={styles.btnSecondary} onClick={() => { setNewInvModal(false); navigate('/quote/new'); }}>Create Quote</button>
              <button style={styles.btnSecondary} onClick={() => { setNewInvModal(false); navigate('/invoice/new'); }}>
                <FiPlus size={14} /> Continue to Create Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {!!toast && (
        <div style={styles.toast}>
          <FiCheckCircle size={16} />
          {toast}
        </div>
      )}
    </div>
  );
}

